/**
 * Public API v1 — TRACKS.
 *
 * The external surface of the tracks domain: the ready set, sections, tasks,
 * agents, the diary, memory and the event stream. Every schema here is authored
 * against the use cases in `apps/backend/src/tracks/application/use-cases/`,
 * which shipped without a public door — this file is that door's contract.
 *
 * ## The wire shapes are the ones the internal mappers already emit
 *
 * `tracks/infrastructure/mappers/*.mapper.ts` build the agent, diary, memory and
 * event payloads for the session-authenticated `/api/tracks/...` surface, and the
 * v1 controllers reuse those mappers rather than mapping a second time. So the
 * schemas below describe the SAME objects, and a v1 controller that parses with
 * them cannot describe a shape the internal surface does not already produce.
 *
 * ## Timestamps are ISO-8601 STRINGS here, and that is not a translation
 *
 * The mappers call `.toISOString()` before the object leaves the backend, so
 * `z.string()` is what the handler actually holds — never `z.date()` narrowed
 * afterwards. The one place a `Date` survives into a use-case result is
 * `TrackTaskPayload.doneAt`, and `V1TracksTasksController` ISOs it at the
 * boundary for the same reason.
 *
 * ## Two units, and only the CHARACTER one is capped here
 *
 * `Track.currentStep` and `TrackTask.acceptance` are bounded in CHARACTERS, and
 * {@link TrackOneLineSchema} refuses an over-length one at this contract so the
 * caller gets a 400 rather than the 500 an unmapped SQLSTATE `23514` produces.
 * It counts code points, which is what `char_length()` counts — never `.max(n)`,
 * which counts UTF-16 code units.
 *
 * `TrackMemory.value` and the per-track budget are bounded in BYTES by
 * `octet_length()`, and this contract caps NEITHER by length: a 2,000-code-unit
 * value can be 6,000 bytes of CJK. That budget is computed once in
 * `tracks/domain/value-objects/track-memory-budget.ts`, whose adapter refuses an
 * overflow with a 409 before the write. Do not flatten the two units.
 */
import { z } from "zod";

import { TRACK_TASK_KINDS } from "../../../../shared/domain/tracks/task-kind";
import { TRACK_NEXT_OWNERS } from "../../../../shared/domain/tracks/task-next-owner";
import { optionalQueryParam } from "./common.schemas";

// ═══════════════════════════════════════════════════════════════
// ENUMS
// ═══════════════════════════════════════════════════════════════

/**
 * Spelled out rather than derived from `DbEnum.TrackDiaryKind`.
 *
 * The value of an enum in a published contract is that it is a SECOND,
 * INDEPENDENT statement of the vocabulary, so `prisma-enum-parity.test.ts` has
 * two things to compare. Deriving it from the generated mirror makes that gate
 * compare a mirror with itself — DRYer, and a tautology.
 */
export const TrackDiaryKindSchema = z.enum([
  "PROGRESS",
  "FINDING",
  "DECISION",
  "PROOF",
  "BLOCKER",
  "QUESTION",
  "KILLED",
  "NOTE"
]);
export type TrackDiaryKind = z.infer<typeof TrackDiaryKindSchema>;

/** Same reasoning as {@link TrackDiaryKindSchema}: independent, not a mirror. */
export const TrackAgentStateSchema = z.enum(["OPEN", "CLOSED", "DEAD", "RETIRED"]);
export type TrackAgentState = z.infer<typeof TrackAgentStateSchema>;

/**
 * Whose turn it is — on a track, and since NEX-4713 on one of its rows too.
 *
 * ⚠️ THE SECOND ENUM HERE THAT IS NOT SPELLED OUT IN PLACE, for exactly
 * {@link TrackTaskKindSchema}'s reason and with the parity argument intact:
 * `TRACK_NEXT_OWNERS` is a HAND-WRITTEN constant in
 * `shared/domain/tracks/task-next-owner.ts`, never the generated `DbEnum` mirror,
 * so `schema.prisma` is still an independent statement to be checked against.
 *
 * It moved there because `task ready` now PARTITIONS on this value, and the
 * partition and the wire shape disagreeing is the divergence this contract exists
 * to prevent. `TRACK_NEXT_OWNER_IS_AGENT_WORKABLE` beside it is the partition.
 */
export const TrackNextOwnerSchema = z.enum(TRACK_NEXT_OWNERS);
export type TrackNextOwner = z.infer<typeof TrackNextOwnerSchema>;

/**
 * What a task row IS: work, or content recorded on the board.
 *
 * ⚠️ THE ONE ENUM HERE THAT IS NOT SPELLED OUT IN PLACE, AND THE PARITY
 * ARGUMENT STILL HOLDS. `TRACK_TASK_KINDS` is a HAND-WRITTEN constant in
 * `shared/domain/tracks/task-kind.ts`, not the generated `DbEnum` mirror — so
 * `prisma-enum-parity.test.ts` still has two independent statements to compare,
 * this one and `schema.prisma`'s. The vocabulary lives there rather than here
 * because the roll-up PARTITIONS on it, and the partition and the wire shape
 * disagreeing is precisely the divergence this contract exists to prevent.
 */
export const TrackTaskKindSchema = z.enum(TRACK_TASK_KINDS);
export type TrackTaskKind = z.infer<typeof TrackTaskKindSchema>;

/**
 * Where the track is in its life. Same reasoning again: independent, not a mirror.
 *
 * 🔴 `DONE` AND `BLOCKED` ARE THE TWO THAT CHANGE WHAT `tracks ready` RETURNS,
 * and neither of them is a second rule. The ready statement carries ONE predicate
 * over this column and `tracks-ready-excludes-blocked-in-one-place.spec.ts`
 * refuses a second executable spelling of it anywhere in the backend — so moving
 * a track to `DONE` takes it out of the ready set through the query that was
 * already there, with nothing added and nothing to invalidate.
 *
 * `IN_REVIEW` is deliberately still ready: work waiting on a reviewer is work
 * somebody can pick up, and a status that quietly hid it would be a second
 * readiness rule wearing a label.
 */
export const TrackStatusSchema = z.enum(["PLANNED", "IN_PROGRESS", "BLOCKED", "IN_REVIEW", "DONE"]);
export type TrackStatus = z.infer<typeof TrackStatusSchema>;

/**
 * `CLOSED`, `DEAD` or `RETIRED`. `OPEN` is not a close.
 *
 * 🚨 DELIBERATELY NOT EXPORTED. `prisma-enum-parity.test.ts` walks every exported
 * `ZodEnum` on this barrel and classifies it against `schema.prisma`; this set
 * shares three of `TrackAgentState`'s four members, which scores 0.75 on the
 * gate's similarity threshold and lands it in the "overlapping" middle that has
 * to be written into `prisma-enum-parity.ledger.ts`. A ledger entry there is a
 * standing claim that a public API deliberately disagrees with the database, and
 * this enum makes no such claim — it is the same vocabulary minus the one member
 * that is not a close. Keeping it module-local states that without spending a
 * ledger row on it. It still reaches every consumer through
 * `CloseTrackAgentBodySchema`, which the descriptor carries.
 */
const TrackAgentCloseStateSchema = z.enum(["CLOSED", "DEAD", "RETIRED"]);

// ═══════════════════════════════════════════════════════════════
// PATH VARS
// ═══════════════════════════════════════════════════════════════

/**
 * How a caller NAMES a track on the path: its `id`, or its `slug`.
 *
 * ## Why both, and why this is not two contracts
 *
 * `slug` is published on {@link TrackSchema}, {@link ReadyTrackSchema} and
 * {@link CreateTrackResponseSchema}, and {@link ReadyTrackSchema}'s own docblock
 * argues it onto the agent loop precisely so a caller can "name a track the way
 * its human names it". Every path var then refused that name and took the uuid
 * only — so the API published a handle it would not answer to. This schema is the
 * repair, and it is ONE schema rather than a second `/by-slug` route because a
 * second route is a second place to gate, a second place to scope to the tenant,
 * and a second place for those two to drift apart.
 *
 * ## 🔴 A LOWERCASE UUID IS A SYNTACTICALLY LEGAL SLUG, SO PRECEDENCE IS PART OF
 * THE CONTRACT AND NOT AN IMPLEMENTATION DETAIL
 *
 * {@link TrackSlugSchema} is `[a-z0-9][a-z0-9-]{0,63}`, which a canonical uuid
 * satisfies — 36 characters of lowercase hex and hyphens. So the two forms
 * OVERLAP, and a resolver that tried the slug first would let a track slugged
 * with another track's uuid shadow it.
 *
 * **The id wins, always.** `TrackAccessGuard` tests `isRequiredUuid` first and
 * only falls through to a slug lookup when that fails. The consequence is worth
 * stating rather than discovering: a track whose slug happens to be uuid-shaped
 * is reachable by its own id and NOT by its slug. That costs nothing across a
 * tenancy boundary — both lookups are scoped to the caller's organisation — and
 * it is the only assignment of precedence that is stable, because the id is the
 * form the server mints and the slug is the form a caller chooses.
 *
 * ⚠️ THE SHAPE IS ONE `ZodString`, DELIBERATELY, AND A `z.union` IS NOT
 * EQUIVALENT. `packages/cli/src/commands/tracks.contract.generated.ts` records
 * every path var as `type: "string"`; a union reaches that generator as a
 * different node and would change ~31 generated rows to say something new about
 * routes whose wire shape has not moved. An alternation inside one regex keeps
 * the generated surface byte-identical, which is what makes this a contract
 * widening rather than a codegen event.
 */
export const TRACK_REF_PATTERN =
  /^(?:[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}|[a-z0-9][a-z0-9-]{0,63})$/;

/** A track's `id` or its `slug`. The id wins when a value could be either. */
export const TrackRefSchema = z
  .string()
  .trim()
  .regex(TRACK_REF_PATTERN, "A track is named by its uuid or by its slug.");

/**
 * 🔴 ONE SCHEMA FOR THE PATH VAR, NOT A WIRE ONE AND A HANDLER ONE.
 *
 * A second, stricter `.uuid()` copy parsed inside the 42 handlers would read as
 * defence in depth and would be unreachable: an unresolvable ref never enters a
 * route at all. `TrackAccessService.canReach` opens with `isRequiredUuid` and
 * answers `false` on anything that is not a uuid, which becomes this domain's
 * ordinary 404 — so by the time a handler runs, `trackId` has either been
 * rewritten to a real uuid by `TrackAccessGuard` or the request is already
 * refused. Unreachable strictness is worse than none: it reads as the thing
 * holding the invariant, and the guard is what actually holds it.
 */
export const TrackIdPathSchema = z.object({ trackId: TrackRefSchema });
export const TrackTaskIdPathSchema = z.object({ taskId: z.string().uuid() });
export const TrackSectionIdPathSchema = z.object({
  trackId: TrackRefSchema,
  sectionId: z.string().uuid()
});
export const TrackAgentIdPathSchema = z.object({
  trackId: TrackRefSchema,
  agentId: z.string().uuid()
});

/**
 * A memory key is a SLUG, and the constraint is not decoration.
 *
 * The delete route addresses the key as a path segment, so a key holding `/`
 * would be unaddressable — a value that can be written and never removed, which
 * is the shape that silently consumes an 8,000-byte budget for ever. Refusing it
 * at the contract is cheaper than encoding it at four call sites and getting one
 * wrong.
 */
export const TrackMemoryKeySchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/, "A memory key is 1-128 chars of [A-Za-z0-9._-].");

export const TrackMemoryKeyPathSchema = z.object({
  trackId: TrackRefSchema,
  key: TrackMemoryKeySchema
});

// ═══════════════════════════════════════════════════════════════
// THE TRACK ITSELF
// ═══════════════════════════════════════════════════════════════

/**
 * A track slug — the tenant-wide human name, unique per organisation.
 *
 * 🔴 NOTHING IN THE DATABASE CHECKS THIS SHAPE, WHICH IS WHY IT IS HERE.
 * `TrackSection.slug` has `Track_section_slug_shape`, so its Zod rule is a second
 * opinion and a typo reaching Postgres is a 23514 rather than a bad row. `Track`
 * carries no such CHECK — only `@@unique([organizationId, slug])` — so this regex
 * is the ONLY thing between a caller and a track named `../../etc` or a slug
 * holding a space. Loosening it does not soften a refusal; it admits the value.
 *
 * Same alphabet as a section slug rather than a wider one, because the two are
 * read side by side by the same people and a second spelling of "slug" in one
 * domain is a thing to get wrong.
 */
export const TrackSlugSchema = z
  .string()
  .trim()
  .regex(/^[a-z0-9][a-z0-9-]{0,63}$/, "A track slug is 1-64 chars of [a-z0-9-].");

/**
 * `Track_currentStep_bounded` and `Track_task_acceptance_bounded`, in one place.
 */
export const TRACK_ONE_LINE_MAX_CHARACTERS = 400;

/**
 * One line of caller-written text — a `currentStep`, a task's `acceptance`.
 *
 * 🔴 THE CAP IS REFUSED HERE BECAUSE A CAP REFUSED ONLY BY THE DATABASE IS A 500.
 * Both CHECK constraints raise SQLSTATE `23514`, which is in neither
 * `TracksExceptionFilter`'s `@Catch(...)` list nor
 * `PrismaClientExceptionFilter.errorCodesStatusMapping`, so a 401-character line
 * fell through `BaseExceptionFilter` and reached the caller as an internal fault
 * — plus one Sentry event per occurrence. A caller-fixable input answered with a
 * 500 teaches a client to retry a request that can never succeed.
 *
 * ## `.refine` RATHER THAN `.max(400)`, AND THE DIFFERENCE IS THE UNIT
 *
 * The CHECK counts CHARACTERS (`char_length`). Zod's `.max(n)` counts UTF-16
 * CODE UNITS — a third unit agreeing with neither the characters here nor the
 * bytes `TrackMemory.value` is bounded in — so `.max(400)` would refuse 400
 * emoji, which are 400 characters and 800 code units and which the database
 * accepts. `[...line].length` iterates CODE POINTS, which is exactly what
 * `char_length()` counts.
 *
 * ## WHY THIS AGREES WITH THE CHECK EXACTLY, RATHER THAN APPROXIMATELY
 *
 * `.trim()` is a TRANSFORM, so the value this refinement measures is the value
 * the controller passes on and the database stores. The CHECK then computes
 * `char_length(btrim(translate(line, <controls>, '     '), ' '))` over that same
 * value — and after a `.trim()` no leading or trailing whitespace survives for
 * either `translate` or `btrim` to remove, so its result is the code-point count
 * this refinement already made. A cap restated in the wrong unit would be the
 * hazard the migration warns about; this one is the same measurement.
 *
 * ## WHAT STAYS IN THE DATABASE
 *
 * The CHECK remains the enforcement and the backstop for every door that is not
 * a v1 body — `import-track-plan.integration.spec.ts` forces its mid-transaction
 * rollback with a 401-character `acceptance` handed straight to the use case.
 * This is the EARLY REFUSAL, on the same argument
 * `track-agent-terminal-reason.ts` makes for the terminal-reason rule.
 *
 * ⚠️ THE BOUND IS A RUNTIME REFUSAL AND NOT A JSON-SCHEMA KEYWORD. `z.refine`
 * adds no `maxLength`, so a generated MCP or CLI client is not told the number.
 * The lower bound's `minLength: 1` is published and this is not, which is a gap
 * in what the contract DECLARES — never in what it enforces.
 *
 * ⚠️ NO ZERO-WIDTH CHARACTER IS WHITESPACE, so a line of one U+200B satisfies
 * this schema and the CHECK alike and renders as an empty line. Recorded in the
 * migration, unclosed here, and do not claim otherwise.
 */
export const TrackOneLineSchema = z
  .string()
  .trim()
  .min(1)
  .refine((line) => [...line].length <= TRACK_ONE_LINE_MAX_CHARACTERS, {
    message: `At most ${TRACK_ONE_LINE_MAX_CHARACTERS} characters, counted as the database counts them.`
  });

/**
 * `Track_short_title_bounded` and `Track_task_short_title_bounded`, in one place.
 */
export const TRACK_SHORT_TITLE_MAX_CHARACTERS = 80;

/** How many words a curated short title may hold. */
export const TRACK_SHORT_TITLE_MAX_WORDS = 5;

/**
 * A hand-written short name for a track or a task — at most five words.
 *
 * `shortTitle` is CURATED, never derived. A `null` means nobody has written one
 * and the consumer falls back to `title`; it never means "the server has not got
 * round to it yet".
 *
 * ## WHY IT IS STORED RATHER THAN TRUNCATED ON READ
 *
 * 🔴 A READ-TIME TRUNCATION CANNOT HONOUR A *WORD* COUNT. Cutting a title at N
 * characters produces N characters, and the one input that most needs shortening
 * — a single 200-character word — truncates to a 200-character "short" title or
 * to a fragment of a word, and there is no N at which both a five-word English
 * title and a space-free CJK one come out right. So the value is written by
 * whoever knows what the thing is called, refused here when it is not short, and
 * returned verbatim.
 *
 * ## TWO RULES, AND ONLY ONE OF THEM IS ALSO IN THE DATABASE
 *
 * The CHARACTER bound is stated twice, in ONE unit, for {@link TrackOneLineSchema}'s
 * reason: the CHECK is the enforcement and the backstop for every door that is
 * not a v1 body, and this is the early refusal that turns a 500 into a 400.
 * `[...s].length` counts CODE POINTS, which is exactly what `char_length()`
 * counts — never `.max(n)`, which counts UTF-16 code units and would be a third
 * unit agreeing with neither.
 *
 * 🔴 THE WORD COUNT LIVES HERE AND NOWHERE ELSE, AND THAT IS DELIBERATE. A
 * Postgres word count is VACUOUS for a space-free script — a CJK short title has
 * one "word" at any length, so the CHECK would admit every string the character
 * bound admits — and it disagrees with a JS count on NBSP across the Postgres
 * versions staging and production run. **Two rules that differ is worse than one
 * rule enforced in one place**, so the database bounds LENGTH and this bounds
 * WORDS. The character bound is what actually stops the long single word, in both
 * places; the word count is what stops five words becoming twelve.
 *
 * ## WHAT COUNTS AS A WORD
 *
 * `.trim()` is a TRANSFORM, so the value this measures is the value that gets
 * stored, and after it no leading or trailing whitespace survives — so splitting
 * on `\s+` yields no empty parts and the count is the number of runs of
 * non-whitespace. JS `\s` includes NBSP and the Unicode space separators, so
 * `"a<U+00A0>b"` is two words here — the character is NAMED rather than pasted,
 * because a literal one is indistinguishable from a space on the page and
 * `no-irregular-whitespace` refuses it outright.
 *
 * ⚠️ NO ZERO-WIDTH CHARACTER IS WHITESPACE, so `"a<U+200B>b"` is ONE word and a
 * short title of one U+200B satisfies both bounds and renders empty — the same
 * gap {@link TrackOneLineSchema} records, and it is not closed here either.
 *
 * ⚠️ NEITHER BOUND IS A JSON-SCHEMA KEYWORD. `z.refine` adds no `maxLength` and
 * has no spelling for a word count at all, so a generated MCP or CLI client is
 * told neither number. That is a gap in what the contract DECLARES, never in what
 * it enforces.
 */
export const TrackShortTitleSchema = z
  .string()
  .trim()
  .min(1)
  .refine((value) => [...value].length <= TRACK_SHORT_TITLE_MAX_CHARACTERS, {
    message: `At most ${TRACK_SHORT_TITLE_MAX_CHARACTERS} characters, counted as the database counts them.`
  })
  .refine((value) => value.split(/\s+/).length <= TRACK_SHORT_TITLE_MAX_WORDS, {
    message: `At most ${TRACK_SHORT_TITLE_MAX_WORDS} words.`
  });

/**
 * Create one track. The door the whole domain was missing.
 *
 * ## `number` IS NOT HERE, AND A CALLER MAY NEVER SEND ONE
 *
 * The per-organisation number is allocated server-side from `TrackNumberSequence`
 * inside the creating transaction, so it is gapless and never collides. A body
 * field for it would let a caller pick a number that the sequence will hand out
 * again later, and `@@unique([organizationId, number])` would then refuse a
 * legitimate create as soon as the counter caught up — a failure landing on
 * somebody who did nothing wrong. `.strict()` refuses `number` by name.
 *
 * ## `status` IS NOT HERE EITHER
 *
 * A track is created `PLANNED`, from the column default. Creating one already
 * `DONE` or `BLOCKED` is a state transition wearing a create's clothes, and
 * {@link SetTrackStatusBodySchema} is the route that performs one. Two doors onto
 * the same column is how they drift, and this is the door where the value would
 * carry no meaning: nothing has happened to the track yet.
 *
 * ## `nextOwnerRef` IS NOT HERE, and that keeps the CHECK trivially satisfied
 *
 * `Track_next_owner_watcher` refuses a `nextOwnerRef` unless `nextOwner` is
 * `EVENT`. Leaving the ref out means the column is always NULL at birth, so the
 * CHECK can never fire on this route and no caller can turn a wiring mistake into
 * a 500. {@link SetTrackNextOwnerBodySchema} is where the watcher is named, and it
 * carries the refinement that turns the illegal pair into a 400.
 */
export const CreateTrackBodySchema = z
  .object({
    /** Unique per organisation. 1-64 chars of `[a-z0-9-]`. */
    slug: TrackSlugSchema,
    title: z.string().trim().min(1),
    /**
     * A hand-written short name, at most five words. `null` or omitted leaves the
     * track uncurated, and a reader falls back to `title`.
     *
     * Bounded by {@link TrackShortTitleSchema} — words here, characters here AND
     * in `Track_short_title_bounded`.
     */
    shortTitle: TrackShortTitleSchema.nullish(),
    /**
     * What happens next, in one line. `null` or omitted until somebody sets one.
     *
     * Capped at {@link TRACK_ONE_LINE_MAX_CHARACTERS} by
     * {@link TrackOneLineSchema}, in the unit `Track_currentStep_bounded` counts.
     */
    currentStep: TrackOneLineSchema.nullish(),
    /** Who is waited on. `USER` when omitted, from the column default. */
    nextOwner: TrackNextOwnerSchema.optional()
  })
  .strict();
export type CreateTrackBody = z.infer<typeof CreateTrackBodySchema>;

/**
 * The track that was created.
 *
 * A projection, on the same argument as {@link ReadyTrackSchema}: `memoryBytes`,
 * `createdByUserId` and the archival columns are not a creator's business, and
 * publishing the row would enrol every column added to `Track` later into this
 * response without anybody deciding to.
 *
 * `number` is the field a caller cannot compute and cannot ask for again cheaply
 * — it is minted by the sequence during this write — so it is the one addition
 * over the ready-set projection that earns its place, alongside the `slug` the
 * caller chose.
 */
export const CreateTrackResponseSchema = z.object({
  id: z.string().uuid(),
  /** Per-organisation, from 1, gapless. Allocated by the server, never sent. */
  number: z.number().int(),
  slug: z.string(),
  title: z.string(),
  /**
   * The curated short name, or `null` when nobody has written one.
   *
   * 🔴 `null` MEANS UNCURATED, AND THE CONSUMER FALLS BACK TO `title`. It is not
   * "pending", it is not a failure, and it is the value every row carried before
   * this field existed — so a surface that renders an empty space for it is
   * rendering the common case. Never truncate `title` to fill the gap: the whole
   * reason the column is stored is that no read-time rule can produce a
   * five-WORD name from an arbitrary title.
   *
   * ⚠️ NEVER `""`. `TrackShortTitleSchema` refuses a blank at the contract and
   * the CHECK refuses one at the column, so "nobody wrote one" is `null` and
   * nothing else. A consumer branching on `""` is branching on a value this API
   * does not produce.
   */
  shortTitle: z.string().nullable(),
  currentStep: z.string().nullable(),
  nextOwner: TrackNextOwnerSchema
});
export type CreateTrackResponse = z.infer<typeof CreateTrackResponseSchema>;

/**
 * Set — or clear — the one line that says what is happening on this track now.
 *
 * 🔴 `null` CLEARS IT, AND THE FIELD IS REQUIRED RATHER THAN OPTIONAL. An
 * OPTIONAL field cannot express "clear it": an omitted key and an explicit
 * `null` would both arrive as `undefined`, and this route would have no spelling
 * for the one thing besides setting it that a caller needs. `.strict()` refuses
 * every other key, so a caller cannot reach any other column through this door.
 *
 * Capped at {@link TRACK_ONE_LINE_MAX_CHARACTERS} by {@link TrackOneLineSchema},
 * the same schema {@link CreateTrackBodySchema} uses — one statement of the rule
 * for the two doors onto one column.
 */
export const UpdateTrackCurrentStepBodySchema = z
  .object({
    /** One line. `null` clears it. */
    currentStep: TrackOneLineSchema.nullable()
  })
  .strict();
export type UpdateTrackCurrentStepBody = z.infer<typeof UpdateTrackCurrentStepBodySchema>;

/** What the track now says, echoed back so a caller needs no second read. */
export const UpdateTrackCurrentStepResponseSchema = z.object({
  trackId: z.string().uuid(),
  currentStep: z.string().nullable()
});
export type UpdateTrackCurrentStepResponse = z.infer<typeof UpdateTrackCurrentStepResponseSchema>;

/**
 * Move the track to a status.
 *
 * 🔴 THIS IS HOW A TRACK FINISHES, AND THERE IS NO DELETE BEHIND IT. A track that
 * is over is `DONE`; its diary, its events and its memory are the record of how
 * the work went, and every one of them is a child of `Track (id, organizationId)`
 * under `ON DELETE CASCADE`, so a delete route would take the record with the
 * row. The domain's scope table lists deletion as out for that reason.
 *
 * ⚠️ NO TRANSITION TABLE, DELIBERATELY. Every status is reachable from every
 * other one, because the work genuinely goes backwards: a track marked `DONE`
 * that turns out not to be, or one parked at `BLOCKED` that unblocks, both need
 * one call rather than a documented escape hatch. A legal-transition matrix here
 * would be a second model of the work that nothing in the product feeds.
 */
export const SetTrackStatusBodySchema = z
  .object({
    /** Where the track is now. `DONE` takes it out of the ready set. */
    status: TrackStatusSchema
  })
  .strict();
export type SetTrackStatusBody = z.infer<typeof SetTrackStatusBodySchema>;

/** What the track now says, echoed back so a caller needs no second read. */
export const SetTrackStatusResponseSchema = z.object({
  trackId: z.string().uuid(),
  status: TrackStatusSchema
});
export type SetTrackStatusResponse = z.infer<typeof SetTrackStatusResponseSchema>;

/**
 * Put a track away, or bring it back.
 *
 * ## 🔴 ARCHIVE, NEVER DELETE — AND THE REASON IS THE DIARY
 *
 * A real delete loses exactly what this domain's immutability protects. Every
 * child of `Track (id, organizationId)` — the diary, the events, the memory —
 * is `ON DELETE CASCADE`, so removing the row removes the record of how the work
 * went, in one statement, with nothing to recover it from. Archiving takes the
 * track out of the ready set and leaves that record readable. The domain's scope
 * table lists deletion as out for this reason, and the diary has no delete route
 * in any spelling.
 *
 * ## 🔴 IT IS REVERSIBLE, AND A ONE-WAY ARCHIVE WOULD BE A DELETE WITH MANNERS
 *
 * `archived: false` brings the track back. An irreversible archive is not a
 * softer delete — it is a delete whose damage is merely harder to see, and it
 * would make one mis-typed uuid unrecoverable on a route whose whole argument is
 * that nothing should be. The un-archive costs one boolean.
 *
 * ## The shape is {@link ToggleTrackTaskBodySchema}'s, deliberately
 *
 * One route, one boolean, both directions — the same shape the task tick already
 * uses. A second `unarchive` path would be a second door onto one column, and
 * two doors onto one column is how they drift.
 *
 * ⚠️ `status` AND `archivedAt` ARE DIFFERENT FACTS AND THIS ROUTE TOUCHES ONE.
 * `DONE` says the work FINISHED; archived says the track was PUT AWAY, which a
 * `PLANNED` track that turned out to be a mistake also is. Both leave the ready
 * set and neither implies the other, so archiving does not write `status` and
 * `SetTrackStatus` does not write `archivedAt`.
 */
export const ArchiveTrackBodySchema = z
  .object({
    /** `true` puts it away, `false` brings it back. */
    archived: z.boolean()
  })
  .strict();
export type ArchiveTrackBody = z.infer<typeof ArchiveTrackBodySchema>;

/** The timestamp the track now carries, or `null` once it is back. */
export const ArchiveTrackResponseSchema = z.object({
  trackId: z.string().uuid(),
  archivedAt: z.string().nullable()
});
export type ArchiveTrackResponse = z.infer<typeof ArchiveTrackResponseSchema>;

/**
 * Say who acts next on this track — the field the guards flip per turn.
 *
 * 🔴 `nextOwner` WAS WRITABLE ONLY AT CREATE, WHICH IS THE ONE MOMENT IT IS
 * LEAST INTERESTING. It is a per-turn handover — an agent finishes and hands back
 * to a person, a person answers and hands back to the agent — so a value that can
 * only be set once is a value that is wrong from the second turn onwards.
 *
 * 🔴 THE REFUSAL BELOW IS THE DATABASE'S CHECK, STATED WHERE A CALLER CAN READ
 * IT. `Track_next_owner_watcher` is `"nextOwner" = 'EVENT' OR "nextOwnerRef" IS
 * NULL`, so a ref sent with `CUE` or `USER` is a 23514 — a Postgres constraint
 * name reaching an API caller as a 500, naming nothing they can act on. This
 * refuses it at the contract with a sentence instead.
 *
 * ⚠️ THE REF IS ALWAYS WRITTEN, NEVER MERGED, AND THAT IS WHAT KEEPS THE CHECK
 * SATISFIED. Omitting `nextOwnerRef` means `null`, so moving a track off `EVENT`
 * clears the watcher in the same statement. A partial update that left the old
 * ref behind would produce exactly the pair the CHECK forbids, on a call that
 * named only `nextOwner`.
 *
 * `EVENT` with no ref is legal — the CHECK admits it — and means the track waits
 * on something outside that nothing has named yet.
 */
export const SetTrackNextOwnerBodySchema = z
  .object({
    /** `CUE` an agent may proceed, `USER` a person must act, `EVENT` something outside. */
    nextOwner: TrackNextOwnerSchema,
    /**
     * The watcher or agent being waited on. Only with `EVENT`; omitted or `null`
     * clears it.
     *
     * ⚠️ NO `.max()`, for the reason this file's header gives: the column is
     * `@db.Text` with no CHECK on its length, and a Zod `.max(n)` would count
     * UTF-16 code units against a bound nothing enforces.
     */
    nextOwnerRef: z.string().trim().min(1).nullish()
  })
  .strict()
  .superRefine((body, ctx) => {
    if (body.nextOwnerRef != null && body.nextOwner !== "EVENT") {
      ctx.addIssue({
        code: "custom",
        path: ["nextOwnerRef"],
        message:
          "nextOwnerRef names what the track is waiting on, so it is only meaningful with " +
          'nextOwner "EVENT". Send it with EVENT, or send nextOwner on its own.'
      });
    }
  });
export type SetTrackNextOwnerBody = z.infer<typeof SetTrackNextOwnerBodySchema>;

/** Both columns, echoed back — including the ref this call may have cleared. */
export const SetTrackNextOwnerResponseSchema = z.object({
  trackId: z.string().uuid(),
  nextOwner: TrackNextOwnerSchema,
  nextOwnerRef: z.string().nullable()
});
export type SetTrackNextOwnerResponse = z.infer<typeof SetTrackNextOwnerResponseSchema>;

/**
 * One track, in full — what `list` returns a page of and `get` returns one of.
 *
 * A PROJECTION, on the same argument as {@link ReadyTrackSchema}, and the fields
 * it adds over that one are the fields the ready set has no business carrying:
 * `status` and `nextOwnerRef` say where the work IS rather than what to do next,
 * and the timestamps are a reader's question rather than an agent loop's.
 *
 * `memoryBytes` and `createdByUserId` stay out of both. Publishing the row would
 * enrol every column added to `Track` later into this response without anybody
 * deciding to — which is the whole reason this domain projects rather than
 * returns entities.
 *
 * ## THERE IS NO TASK COUNT HERE, AND THAT IS A DECISION RATHER THAN A GAP
 *
 * 🔴 A TRACK WITH NO TASKS READS EXACTLY LIKE A TRACK WITH TWENTY, FROM THIS
 * SCHEMA ALONE (NEX-4547). The answer is {@link ListTrackRollupsResponseSchema}
 * — `GET /tracks/rollup?trackIds=…`, up to {@link TRACK_ROLLUP_BATCH_MAX} ids in
 * ONE request — so a page of this schema plus one more call tells a reader which
 * of those tracks is empty, which is all content, and which is real work part
 * done. An emptiness flag here would answer strictly less than that and would
 * cost a second call to explain itself the moment anyone asked "empty of what".
 *
 * ⚠️ AND A `taskCount` HERE WOULD BE A SECOND PRODUCER OF A NUMBER THIS PRODUCT
 * HAS EXACTLY ONE OF. `computeTaskRollup` is the single implementation of the
 * leaf rule and `__governance__/tracks-rollup-has-one-implementation.spec.ts`
 * refuses a second site that counts tasks — a Prisma `count` on the `trackTask`
 * delegate is one of the four shapes it scans for, and serving a count on the
 * catalog read is precisely that. The donor already shipped two disagreeing
 * progress numbers once; this is the rule written to stop the third.
 */
export const TrackSchema = z.object({
  id: z.string().uuid(),
  /** Per-organisation, from 1, gapless. */
  number: z.number().int(),
  slug: z.string(),
  title: z.string(),
  /**
   * The curated short name, or `null`. See {@link CreateTrackResponseSchema} for
   * what a `null` obliges a reader to do.
   */
  shortTitle: z.string().nullable(),
  status: TrackStatusSchema,
  /** One line, at most 400 characters. `null` until somebody sets one. */
  currentStep: z.string().nullable(),
  nextOwner: TrackNextOwnerSchema,
  /** Only ever set with `nextOwner: "EVENT"`. */
  nextOwnerRef: z.string().nullable(),
  /**
   * When the track was put away, or `null` while it is live.
   *
   * 🔴 IT IS PUBLISHED BECAUSE ARCHIVING IS REVERSIBLE AND OTHERWISE INVISIBLE.
   * An archived track is absent from `ready` AND from the default page of
   * `list`, so without this field a caller holding the id cannot tell a live
   * track from one somebody put away — and would have no way to know there was
   * anything to un-archive.
   */
  archivedAt: z.string().nullable(),
  /**
   * When the track entered its current `DONE` state, or `null` at every other
   * status. The database refuses the other combination outright
   * (`Track_done_at_only_when_done`), so a non-null value here always travels
   * with `status: "DONE"`.
   *
   * 🔴 A CURRENT-STATE PROJECTION, NEVER A HISTORY, AND A CONSUMER COUNTING
   * SHIPPED WORK HAS TO KNOW THAT. Every status is reachable from every other
   * one, so re-opening a track clears this field: the track leaves any window it
   * was previously counted in, RETROACTIVELY, and nothing records that it was
   * ever in it. "What finished last week" answered from this field means "what
   * is finished NOW and finished last week", which is a defensible number and is
   * not a stable one.
   *
   * ⚠️ `null` ON A `DONE` TRACK IS LEGAL AND IS NOT AN ERROR. Tracks that reached
   * DONE before this field existed carry no completion time — there was no honest
   * value to give them and none was invented. Treat the field as absent, never as
   * zero or as epoch.
   */
  doneAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string()
});
export type Track = z.infer<typeof TrackSchema>;

/**
 * Every track in the organisation, in `number` order, bounded by `limit`.
 *
 * 🔴 THIS IS NOT THE READY SET AND MUST NOT BE READ AS ONE. `ready` answers "what
 * can be worked on"; this answers "what exists". A `DONE` track is absent from the
 * first and present here — which is the whole point of having both, because a
 * caller that finished a track has no other way to see it again.
 *
 * `status` narrows the page when it is sent. Omitted, every status is returned.
 */
/**
 * The page cursor's grammar, and it is DECLARED HERE because the token is part
 * of the published contract rather than an implementation detail of one adapter.
 *
 * ## `number` is the key, and `@@unique([organizationId, number])` is why
 *
 * A keyset needs a TOTAL order or it needs a tiebreaker, and `Track.number` is
 * already total within an organisation — the unique constraint says so, and
 * `TrackNumberSequence` allocates it gaplessly inside the creating transaction.
 * Nothing renumbers a track after birth (`insert-track.prisma.adapter.ts` is the
 * only writer of the column), so a cursor taken today names the same boundary
 * tomorrow. That is the property an offset does not have and the reason this is
 * a keyset: with `LIMIT/OFFSET`, one track archived above the cursor shifts every
 * later row up by one and the next page SKIPS one, silently.
 *
 * ## The fingerprint is the fence, not decoration
 *
 * A cursor is a position INSIDE a filtered set. Round-tripping one from a
 * `status=PLANNED` page into a `status=DONE` request would return a page that is
 * correctly scoped, correctly counted, and starts in the middle — a plausible
 * wrong answer with nothing to raise. So the token carries the filters it was
 * issued under and a mismatch is refused with a 400 that says which changed.
 *
 * 🔴 IT IS SERVER-ISSUED AND ITS SHAPE IS NOT PART OF THE PROMISE. Round-trip
 * `nextCursor` verbatim; do not build one. The grammar below is what the server
 * emits today and may change without a major version — the fingerprint is what
 * makes a hand-rolled token fail loudly instead of paging somebody's dashboard
 * from the wrong place.
 *
 * ## Adding a filter INVALIDATES every cursor already issued, on purpose
 *
 * 🔴 A NEW SEGMENT MAKES EVERY OUTSTANDING TOKEN `malformed`, AND THAT IS THE
 * CORRECT OUTCOME RATHER THAN A COST TO ENGINEER AROUND. A token minted under a
 * two-filter fingerprint carries no statement about the third, so honouring it
 * would resume a walk under a filter set it was never issued for — the exact
 * plausible-wrong-page this fence exists to refuse. `malformed` rather than
 * `filters-changed` because the server genuinely did not issue this shape; the
 * caller's fix is the same either way, which is to drop the cursor and re-read
 * from the top.
 *
 * ⚠️ THE FINGERPRINT IS CAPTURED AS ONE GROUP AND COMPARED AS ONE STRING, so the
 * comparison below has no idea how many segments there are. That is deliberate:
 * a decoder that destructured the segments individually would have to grow every
 * time a filter lands, and forgetting it compares a PREFIX of the fingerprint
 * against the whole of it — which is a cursor accepted under the wrong filters,
 * silently. Adding a filter now touches the regex and
 * {@link trackListFingerprint}, and nothing else here.
 */
const TRACK_LIST_CURSOR = /^(\d+)~((?:[A-Z_]+|\*)\.(?:exclude|only|include)\.(?:[A-Z_]+|\*))$/;

/** The filters a page was read under, as they appear inside a cursor. */
export interface TrackListFilters {
  readonly status?: TrackStatus;
  readonly archived?: "exclude" | "only" | "include";
  readonly nextOwner?: TrackNextOwner;
}

/**
 * The filters, as one comparable string.
 *
 * `*` for "every status" rather than the empty string: an absent filter and a
 * filter somebody cleared have to render the same way, and an empty segment
 * makes the two adjacent delimiters collapse in a way a reader misreads.
 * `nextOwner` takes the same `*`, and for the same reason.
 */
export const trackListFingerprint = (filters: TrackListFilters): string =>
  `${filters.status ?? "*"}.${filters.archived ?? "exclude"}.${filters.nextOwner ?? "*"}`;

/** The token that resumes AFTER the track carrying `number`. */
export const encodeTrackListCursor = (afterNumber: number, filters: TrackListFilters): string =>
  `${afterNumber}~${trackListFingerprint(filters)}`;

/**
 * The `number` a page resumes after, or a reason it cannot.
 *
 * Two failures, deliberately distinguished, because they need different words to
 * the caller: a token this server never issued, and a token issued under other
 * filters.
 */
export type TrackListCursorRead =
  | { readonly ok: true; readonly afterNumber: number }
  | { readonly ok: false; readonly reason: "malformed" | "filters-changed" };

export const decodeTrackListCursor = (
  token: string,
  filters: TrackListFilters
): TrackListCursorRead => {
  const match = TRACK_LIST_CURSOR.exec(token);
  if (match === null) return { ok: false, reason: "malformed" };

  const [, rawNumber, fingerprint] = match;
  if (fingerprint !== trackListFingerprint(filters)) {
    return { ok: false, reason: "filters-changed" };
  }

  const afterNumber = Number(rawNumber);
  // The regex admits `\d+`, so this is about MAGNITUDE rather than shape: a
  // 400-digit cursor parses to `Infinity`, which compares as greater than every
  // row and returns an empty page that reads exactly like "you reached the end".
  if (!Number.isSafeInteger(afterNumber)) return { ok: false, reason: "malformed" };

  return { ok: true, afterNumber };
};

export const ListTracksQuerySchema = z.object({
  limit: z.coerce.number().int().positive().max(200).optional(),
  /**
   * Resume after a previous page. Pass `nextCursor` back VERBATIM.
   *
   * `optionalQueryParam` rather than a bare `.optional()`, and the difference is
   * the first page: a client that clears its cursor sends `?cursor=`, which `qs`
   * parses as the empty string, and a bare `.optional()` answers 400 on the one
   * request every caller makes first.
   */
  cursor: optionalQueryParam(z.string().min(1)),
  status: TrackStatusSchema.optional(),
  /**
   * What to do about archived tracks. `exclude` when omitted.
   *
   * 🔴 A TRI-STATE ENUM RATHER THAN A BOOLEAN, AND THAT IS NOT A STYLE CHOICE.
   * This is a QUERY parameter, so it arrives as a string, and `z.coerce.boolean()`
   * follows JavaScript truthiness — `"false"` is a non-empty string and coerces
   * to `true`. A caller writing `?archived=false` would get the exact opposite
   * of what they asked for, with no error anywhere. Naming the three outcomes
   * removes the coercion entirely.
   *
   * 🔴 `only` IS THE RECOVERY PATH, AND WITHOUT IT ARCHIVING WOULD BE A DELETE.
   * An archived track is out of `ready` and out of this list's default page, so
   * `only` is how somebody finds a track they put away by mistake and hands it
   * back to `POST /tracks/:trackId/archive` with `archived: false`.
   */
  archived: z.enum(["exclude", "only", "include"]).optional(),
  /**
   * Narrow to the tracks waiting on one kind of actor. Omitted returns every
   * one, exactly as `status` does.
   *
   * 🔴 THIS IS THE FILTER A "WAITING ON ME" BOARD CANNOT ASSEMBLE CLIENT-SIDE.
   * The page is a keyset bounded by `limit` (200 at most), so a client filtering
   * what it has already fetched is filtering ONE PAGE — and on any organisation
   * past that page the panel under-reports, rendering as "nobody is waiting on
   * you". A screen that quietly says the wrong thing is worse than an absent
   * one, which is why this narrows the SQL rather than the response.
   *
   * ⚠️ ONE VALUE, NOT A SET, AND `nextOwner` IS NOT A PERSON. The column is
   * `NOT NULL DEFAULT 'USER'` over a three-value enum — who acts next by KIND
   * (a human, Cue, an external event), never which human. So "waiting on me" is
   * `nextOwner=USER`, and there is no null row for this filter to have an
   * opinion about. A set-valued filter would buy exactly one query a single
   * value cannot express (two of the three) and would have to canonicalise its
   * ordering inside the cursor fingerprint, or two spellings of one set would
   * compare unequal and 400 a legitimate resume.
   *
   * ⚠️ IT JOINS THE CURSOR FINGERPRINT, so a page walked under one value and
   * resumed under another is refused rather than served the middle of a
   * different list. See {@link trackListFingerprint}.
   */
  nextOwner: TrackNextOwnerSchema.optional()
});
export type ListTracksQuery = z.infer<typeof ListTracksQuerySchema>;

/**
 * A page of tracks, with the two numbers a caller needs to page it and to know
 * how big the answer is.
 *
 * 🔴 `total` AND `nextCursor` DESCRIBE THE SAME FILTERED POPULATION, BY
 * CONSTRUCTION RATHER THAN BY CARE. Both reads build their `where` from ONE
 * function — `trackListWhere` in the backend's tracks persistence layer — so the
 * count and the page cannot come to disagree about which tracks are in scope. A
 * total a caller can never page to is worse than no total at all: it makes a
 * dashboard show a number nobody can reach and gives nothing to raise.
 *
 * 🔴 THESE ARE ON THE DATA OBJECT, NOT ON THE ENVELOPE'S `meta`, AND THAT IS A
 * MEASURED CHOICE. `packages/sdk/src/response-contract.generated.ts` projects
 * each route's DATA payload and its `required` set and NOTHING of the envelope —
 * `"GET /tracks"` reads `payload:{fields:{"tracks":"a"}}` — so a `total` passed
 * as `createApiSuccess`'s second argument is invisible to every SDK consumer and
 * to the generated contract's own staleness gate. `PaginationMetaSchema` is also
 * the wrong shape here: its `page` and `totalPages` are required and mean nothing
 * for a keyset, and emitting `page: 1` forever would be a fabricated field. The
 * house contract that already got a cursor'd list right is
 * `GetMessagesResponseSchema` — `{ messages, hasMore, nextBefore }`, all in the
 * data — and this follows it.
 */
export const ListTracksResponseSchema = z.object({
  tracks: z.array(TrackSchema),
  /**
   * Every track matching `status`, `archived` and `nextOwner`, ignoring `limit`
   * and `cursor`.
   *
   * Not the size of this page — `tracks.length` is that, and a caller that wants
   * to know whether it has read everything reads `hasMore`.
   *
   * 🔴 SO IT IS ALSO THE COUNT A "WAITING ON ME" BADGE RENDERS. `?nextOwner=USER
   * &limit=1` costs one statement pair and answers "how many are on me", with no
   * paging at all.
   *
   * ⚠️ IT COUNTS WHAT THIS CALLER CAN REACH, NEVER THE ORGANISATION. The page and
   * the count are built from ONE filter expression, and that expression carries
   * the caller's accessible track ids whenever the caller is grant-limited — a
   * JWT member, a personal token, a platform-operator key or an agent-skill key.
   * Only an org admin and an org-bound key with the scope see the whole tenant.
   * Reading it as an organisation-wide figure makes two callers asking the
   * identical question look like a data bug, when both answers are correct.
   */
  total: z.number().int(),
  /** `true` when a further page exists under the same filters. */
  hasMore: z.boolean(),
  /**
   * The token for the next page, or `null` when `hasMore` is false.
   *
   * Always page with this rather than deriving a cursor from the last returned
   * track: the token carries the filter set it was issued under, and a
   * hand-rolled one is refused rather than quietly resuming somewhere else.
   */
  nextCursor: z.string().nullable()
});
export type ListTracksResponse = z.infer<typeof ListTracksResponseSchema>;

/**
 * A track's progress: leaves done, leaves total.
 *
 * 🔴 COUNTS, NEVER A PERCENTAGE, and that is the server's decision rather than
 * an omission. `computeTaskRollup` is the ONE implementation of the leaf rule
 * for the whole product; rounding here would be a second display decision taken
 * in the wrong place, and a caller handed a percentage cannot recover the
 * counts. Divide them yourself.
 *
 * ⚠️ LEAVES ONLY, at any nesting depth. A parent task is structure rather than
 * work, so it is in neither number — a track of one parent holding three
 * children reads `0/3`, never `0/4`.
 *
 * ⚠️ STEP LEAVES ONLY. A `DECISION` or a `DEFINITION` is content recorded on the
 * board, never outstanding work, so it is in neither number either — and a STEP
 * whose only children are content is a LEAF rather than structure, so the work
 * it names does not vanish. {@link TrackTaskKindSchema} is the vocabulary and
 * `computeTaskRollup` is the one place it is applied.
 *
 * 🔴 `0/0` MEANS A REAL, READABLE TRACK WITH NO WORK ON IT — AND NOTHING ELSE.
 * A track id this caller cannot reach is a 404 from `TrackAccessGuard`, which
 * runs before the handler on both transports. That refusal is ONE answer for an
 * absent id, another organisation's and an ungranted one, so splitting *empty*
 * from *unreachable* costs no tenancy: the oracle would be in telling FOREIGN
 * from ABSENT, and the gate refuses to.
 *
 * ⚠️ SO DO NOT ADD A FIELD THAT DISTINGUISHES THEM HERE. There is nothing left
 * for it to say — the status code already did — and a body that named the
 * difference would rebuild the oracle one layer below the gate that closed it.
 *
 * ⚠️ THE BATCHED TWIN IS THE OPPOSITE CASE AND ITS `0/0` IS STILL LOAD-BEARING.
 * {@link ListTrackRollupsResponseSchema} names its tracks in a QUERY rather than
 * in the path, so no guard can see them and every id asked about comes back
 * `0/0` whether it resolves or not. Read that schema's own note before carrying
 * a conclusion from this one across.
 */
export const TrackRollupSchema = z.object({
  done: z.number().int(),
  total: z.number().int(),
  /**
   * Every task of the track, by kind — parents included, done and open alike.
   *
   * 🔴 IT IS PUBLISHED BECAUSE `done`/`total` NARROWED, AND A NUMBER THAT
   * NARROWS WITHOUT SAYING WHAT IT DROPPED IS A NUMBER NOBODY CAN CHECK. Before
   * `kind` existed these two counted every leaf; they now count STEP leaves, so
   * a caller watching a denominator fall needs to see where it went. The sum of
   * this object is the whole task set, and the gap between it and `total` is
   * exactly the structure and the content.
   *
   * ⚠️ IT IS A CENSUS, NOT A SECOND ROLL-UP. No leaf rule and no done test is
   * applied to it, so it cannot become the second implementation of either — the
   * defect `computeTaskRollup` exists to prevent.
   *
   * ⚠️ EVERY KEY IS ALWAYS PRESENT, `0` INCLUDED. An absent key would make a
   * caller reading `byKind.DECISION` get `undefined` on the ordinary track that
   * has none, and `undefined` arithmetic is how a display shows `NaN`.
   */
  byKind: z.record(TrackTaskKindSchema, z.number().int())
});
export type TrackRollup = z.infer<typeof TrackRollupSchema>;

/**
 * How many tracks one batched roll-up call may name.
 *
 * 🔴 100 RATHER THAN THE PAGE CEILING OF 200, AND THE BOUND IS THE URL. 200
 * uuids comma-separated is ~7.4 KB of query string, and nginx's default
 * `large_client_header_buffers` gives the whole request line ONE 8 KB buffer — a
 * margin of a few hundred bytes, and a 414 that appears only for the largest
 * pages is the worst kind of intermittent. 100 ids is ~3.7 KB, half the buffer.
 *
 * So a full 200-track page is two roll-up calls rather than one. That is the
 * cost, stated: the defect being removed is 1 + N requests for N tracks, and
 * 1 + 2 is not it.
 */
export const TRACK_ROLLUP_BATCH_MAX = 100;

/**
 * The tracks to roll up.
 *
 * 🔴 BOTH QUERY SPELLINGS ARE ACCEPTED, AND THAT IS A TRANSPORT FACT RATHER THAN
 * A CONVENIENCE. Express parses with `qs`, so `?trackIds=a,b` arrives as a
 * STRING and `?trackIds=a&trackIds=b` arrives as an ARRAY — two shapes for one
 * intention, and a caller has no way to know which this route wants. A schema
 * accepting only the first answers 400 to a perfectly ordinary request. The SDK
 * sends the comma form; a hand-written client sends whichever its HTTP library
 * builds.
 *
 * ⚠️ IT SPLITS BEFORE IT VALIDATES, so a malformed id is a 400 naming the id
 * rather than a silently shorter answer. Duplicates are collapsed: the response
 * is keyed by track id, so a repeated id cannot produce a second entry and
 * counting it against the cap would refuse a call this route can serve.
 */
export const TrackRollupsQuerySchema = z.object({
  trackIds: z
    .union([z.string(), z.array(z.string())])
    .transform((raw) => {
      const parts = (Array.isArray(raw) ? raw : [raw]).flatMap((entry) => entry.split(","));
      return [...new Set(parts.map((id) => id.trim()).filter((id) => id.length > 0))];
    })
    .pipe(z.array(z.string().uuid()).min(1).max(TRACK_ROLLUP_BATCH_MAX))
});
export type TrackRollupsQuery = z.infer<typeof TrackRollupsQuerySchema>;

/**
 * One roll-up per track ASKED FOR, not per track found.
 *
 * ⚠️ A TRACK THAT IS NOT YOURS, OR THAT DOES NOT EXIST, IS PRESENT WITH `0/0` —
 * exactly what `GET /tracks/:trackId/rollup` answers for the same id, and for the
 * same reason: omitting it would make the response an existence oracle for other
 * tenants' track ids. A caller reading a missing key as "absent" would be reading
 * a distinction this route deliberately does not draw.
 */
export const ListTrackRollupsResponseSchema = z.object({
  rollups: z.array(
    TrackRollupSchema.extend({
      trackId: z.string().uuid()
    })
  )
});
export type ListTrackRollupsResponse = z.infer<typeof ListTrackRollupsResponseSchema>;

// ═══════════════════════════════════════════════════════════════
// READY SET
// ═══════════════════════════════════════════════════════════════

/**
 * One row of the TRACK-level ready set.
 *
 * A projection rather than the `Track` row, and the reason is a leak rather than
 * tidiness: `ready` is the whole agent loop in one call, so this shape is what an
 * autonomous caller reads on every iteration. Publishing the full row would put
 * `memoryBytes`, `createdByUserId` and the archival columns into that loop, and
 * every column added to `Track` afterwards would join them silently.
 */
export const ReadyTrackSchema = z.object({
  id: z.string().uuid(),
  number: z.number().int(),
  /**
   * The name a person types and reads.
   *
   * 🔴 IT IS ON THIS ROW BECAUSE THE ROW IS WHERE A CALLER PICKS A TRACK. The
   * ready set shipped with `id`, `number` and `title` and no slug, so an agent
   * reading its own work back could not name a track the way its human names it
   * — and the number, which is the only other short handle, is minted by the
   * server and means nothing outside this organisation.
   */
  slug: z.string(),
  title: z.string(),
  /**
   * The curated short name, or `null`. See {@link CreateTrackResponseSchema}.
   *
   * It travels on the agent loop's own row for the reason `slug` does: this is
   * where a caller PICKS a track, and the shortest true name for it is the one
   * thing a caller cannot derive from anything else on the row.
   */
  shortTitle: z.string().nullable(),
  /** One line, at most 400 characters. `null` until somebody sets one. */
  currentStep: z.string().nullable(),
  nextOwner: TrackNextOwnerSchema
});
export type ReadyTrack = z.infer<typeof ReadyTrackSchema>;

export const ListReadyTracksResponseSchema = z.object({
  tracks: z.array(ReadyTrackSchema),
  /**
   * `true` when the ready set is larger than this page.
   *
   * 🔴 WITHOUT IT A TRUNCATED PAGE AND A COMPLETE ANSWER WERE THE SAME RESPONSE,
   * AND NO CLIENT COULD TELL THEM APART. `clampReadySetLimit` defaults to 50, so
   * an organisation with more ready tracks than that got 50 rows and no signal —
   * and because the statement orders by `Track.number` ASCENDING while a new
   * track takes the HIGHEST number, the rows silently dropped were always the
   * NEWEST. A caller was most likely to lose the track it had just made.
   *
   * Derived by reading one row past the page and discarding it, exactly as
   * `ListTracksResponseSchema` does — no second statement, and no count.
   *
   * ⚠️ THERE IS DELIBERATELY NO `total` AND NO `nextCursor` HERE, AND THE REASON
   * IS NOT COST. Both would need a second statement of the readiness predicate,
   * which is a RELEASE rule this domain keeps to exactly one implementation; and
   * a keyset cursor over a set that is re-derived on every read silently skips a
   * row that became ready after the token was minted. `hasMore` answers the only
   * question a caller can act on — raise `limit` (ceiling 200) and re-read from
   * the top, which is always self-consistent.
   */
  hasMore: z.boolean()
});
export type ListReadyTracksResponse = z.infer<typeof ListReadyTracksResponseSchema>;

/**
 * One row of the TASK-level ready set within a track.
 *
 * `gate` travels with the row on purpose: a caller deciding what to pick up needs
 * to know a task will refuse its own completion without evidence BEFORE it
 * starts, not at the moment it ticks the box.
 */
export const ReadyTrackTaskSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  /**
   * The curated short name, or `null`. See {@link CreateTrackResponseSchema}.
   */
  shortTitle: z.string().nullable(),
  /** The acceptance criterion, one line. `null` when nobody wrote one. */
  acceptance: z.string().nullable(),
  /** `true` when ticking this task requires evidence. */
  gate: z.boolean(),
  /**
   * Whose turn it is on this row.
   *
   * 🔴 IT TRAVELS ON EVERY ROW OF **BOTH** HALVES, AND THAT IS WHAT MAKES THE
   * BINARY SPLIT LOSSLESS. `waiting` merges `USER` and `EVENT` because the
   * question the agent loop asks is binary; this field is how a caller that cares
   * about the difference gets it back without a second call.
   *
   * ⚠️ NOT A PERSON AND NOT A PERMISSION. `CUE` means an agent may proceed, `USER`
   * that a human has to act, `EVENT` that something outside has to happen first.
   * Access is `TrackAccessGuard`; this column grants and refuses nothing.
   */
  nextOwner: TrackNextOwnerSchema
});
export type ReadyTrackTask = z.infer<typeof ReadyTrackTaskSchema>;

/**
 * A track's ready rows, SPLIT BY WHOSE TURN IT IS.
 *
 * ## 🔴 `tasks` SURVIVES AS A DEPRECATED UNION, AND THE TWO NEW SETS ARE THE ANSWER
 *
 * The ticket is not that the owner was unknowable — it was recoverable by reading
 * each title and acceptance by eye — it is that **a board summed a list it should
 * have split**: "29 ready" over a set in which 23 rows needed a human decision and
 * 6 were workable. `nextOwner` on the row cannot prevent a `.length`, so the answer
 * is a shape that offers the split directly.
 *
 * ⚠️ DELETING `tasks` WAS THE FIRST DESIGN AND IT WAS WRONG FOR A REASON THE
 * TYPECHECKER CANNOT SEE. Inside this monorepo the removal is safe and even
 * pleasant: every consumer reds and has to say which question it is asking. But
 * this file is the EXTERNAL surface, no typechecker reaches a caller outside it,
 * and a published CLI talks to a server that deploys before anyone upgrades. The
 * in-repo benefit is banked anyway — `ReadyTrackTaskPage`, the use case's own
 * result, carries NO `tasks` member, so every backend and CLI call site was still
 * forced to choose. Only the wire carries the compatibility field.
 *
 * 🔴 IT DEPARTS FROM THE TRACK-LEVEL PRECEDENT ON PURPOSE (#4586).
 * {@link ListReadyTracksResponseSchema} is one flat list with `nextOwner`
 * projected on each row, and that is right THERE: `findReadyTracks` merely
 * projects the column and `list-tracks` narrows only on an explicit
 * `?nextOwner=`, so the track-level shape can never remove a row from a read
 * nobody filtered. `task ready` IS the agent loop's default read — the one that
 * answers "what do I do next" — so here the SHAPE decides what every unfiltered
 * caller sees. `TrackTask.nextOwner`'s docblock in `schema.prisma` records the
 * same split from the column's side.
 *
 * ## Two halves, one statement, one page
 *
 * 🔴 THE SPLIT IS A PROJECTION OVER ONE `$queryRaw`, NEVER A SECOND QUERY. An
 * owner predicate in SQL would need the readiness anti-join stated a second time,
 * which is the RELEASE rule this domain keeps to exactly one implementation — the
 * same reason {@link ListReadyTracksResponseSchema} refuses a `total`.
 */
export const ListReadyTrackTasksResponseSchema = z.object({
  /**
   * Every ready row, both halves, in the server's own order.
   *
   * 🔴 DEPRECATED, AND KEPT ONLY SO THIS CHANGE IS PURELY ADDITIVE. Counting it is
   * the defect NEX-4713 reports: it is "how much is unblocked", never "how much can
   * be worked on", and one board read 29 here when 6 were workable. Read
   * {@link ListReadyTrackTasksResponseSchema.shape.workable} instead.
   *
   * 🚨 IT IS NOT REMOVED, AND THE REASON IS A MEASURED ONE RATHER THAN CAUTION.
   * `@agent-nexus/cli` (1.1.0) and `@agent-nexus/sdk` (2.1.0) are both published to
   * npm and both read this field; `tracks_list_ready_tasks` is an MCP tool over the
   * same route. **The server deploys before anybody upgrades a CLI**, so removing
   * this key would break every installed copy at ITS runtime, with no signal on our
   * side. The one precedent in this repo for deleting a v1 response field
   * (`ExecutionNodeResult.logs`, `ExecutionOutput.outputType`) rests its whole
   * argument on "no caller can be relying on a value, because there has never been
   * one" — those answered `null` on every request for their entire life. This field
   * has been real and populated since the route shipped, so that argument does not
   * reach it.
   *
   * ⚠️ ITS ORDER CHANGED EVEN THOUGH ITS CONTENTS DID NOT. The statement now sorts
   * workable rows to the front of the page, so this is `workable` then `waiting`
   * rather than pure `position`. Nothing that builds a Set or a length notices; a
   * consumer that took `tasks[0]` as "the next thing to do" now gets a better answer
   * rather than a worse one.
   */
  tasks: z.array(ReadyTrackTaskSchema),
  /**
   * The rows an agent may pick up right now — ready by dependency AND
   * `nextOwner: "CUE"`.
   *
   * 🔴 THIS IS THE ONLY ARRAY WHOSE LENGTH ANSWERS "HOW MUCH CAN BE WORKED ON",
   * and it is the number the ticket's board reported wrongly as 29.
   */
  workable: z.array(ReadyTrackTaskSchema),
  /**
   * The rows that are ready by dependency but are NOT the agent's turn — every
   * blocker satisfied, waiting on a person (`USER`) or on something outside
   * (`EVENT`).
   *
   * 🔴 THEY ARE PUBLISHED RATHER THAN FILTERED AWAY, WHICH IS THE TICKET'S OWN
   * WORDING: a row set to `USER` "stays visible without pretending to be
   * workable". Dropping them would trade one wrong number for a different wrong
   * number — a board that cannot see what it is waiting on cannot chase it, and
   * `task why-not-ready` would report every one of them as BLOCKED, which they are
   * not.
   */
  waiting: z.array(ReadyTrackTaskSchema),
  /**
   * `true` when this track has more ready rows than this page — across BOTH
   * halves, because they are one page of one statement.
   *
   * The same signal, and the same derivation, as
   * {@link ListReadyTracksResponseSchema}'s — which owns the reasoning, including
   * why there is no `total` and no `nextCursor`.
   *
   * 🔴 THIS IS THE ROUTE WHERE TRUNCATION IS ALREADY REACHABLE IN PRODUCTION.
   * Measured 2026-08-30: the largest organisation holds 12 OPEN tracks against a
   * default page of 50, so the track-level set cannot truncate today — but one
   * track holds 165 tasks, and a plan whose steps are mostly unblocked puts more
   * than 50 of them in the ready set at once.
   *
   * 🔴 A PARTITIONED PAGE COULD STARVE THE HALF THAT MATTERS, AND THE STATEMENT'S
   * `ORDER BY` IS WHAT STOPS IT. `findReadyTasks` sorts workable rows to the FRONT
   * of the page before position, so a page that is cut always drops `waiting` rows
   * first. The property that buys: **`workable` is complete unless it alone filled
   * the page** — `workable.length < limit` means you are seeing all of it, however
   * many `waiting` rows fell off. Without that key, a track with 200 `USER` rows
   * and 5 `CUE` rows would answer with an empty `workable` and `hasMore: true`,
   * and an agent would read a board with work on it as having nothing to do.
   */
  hasMore: z.boolean()
});
export type ListReadyTrackTasksResponse = z.infer<typeof ListReadyTrackTasksResponseSchema>;

/**
 * How many rows a ready-set page may ask for.
 *
 * The bound is restated here because it is the only thing that bounds the
 * query's cost, and a caller that cannot see the ceiling asks for a million.
 * `clampReadySetLimit` is the authority and clamps whatever arrives; this is the
 * early refusal that gives the caller a 400 it can read instead of a silently
 * smaller page.
 */
export const ReadySetQuerySchema = z.object({
  limit: z.coerce.number().int().positive().max(200).optional()
});
export type ReadySetQuery = z.infer<typeof ReadySetQuerySchema>;

// ═══════════════════════════════════════════════════════════════
// SECTIONS
// ═══════════════════════════════════════════════════════════════

export const TrackSectionSchema = z.object({
  id: z.string().uuid(),
  trackId: z.string().uuid(),
  parentSectionId: z.string().uuid().nullable(),
  slug: z.string(),
  /** The materialised path, `parent/child`. Every resolution reads this column. */
  path: z.string(),
  title: z.string(),
  position: z.number().int(),
  /**
   * The section's prose. `""` when nobody has written any.
   *
   * 🔴 IT IS PUBLISHED BECAUSE WITHOUT IT A READER HAS AN OUTLINE AND NO
   * DOCUMENT. The column has been writable since the domain shipped —
   * `CreateTrackSectionBodySchema` takes it — and no route has ever returned it,
   * so a surface rendering the section tree could show every title and none of
   * the content underneath. A field that can be written and never read is a
   * field whose writer has no way to check what it stored.
   *
   * ⚠️ NEVER `null`. The column is `@db.Text @default("")` and NOT NULL, so
   * "nobody wrote any" is the empty string. A consumer branching on `null` here
   * is branching on a value this API does not produce.
   */
  body: z.string()
});
export type TrackSection = z.infer<typeof TrackSectionSchema>;

/**
 * Every section of one track — the document tree, prose included.
 *
 * ⚠️ ORDERED BY THE MATERIALISED `path`, SO EVERY PARENT ARRIVES BEFORE ITS
 * CHILDREN and a client can build the tree in one pass without sorting. `path`
 * is `parent/child`, so string order IS depth-first order; `position` decides
 * SIBLING order and travels on each row, because two siblings sort by slug
 * under `path` and by `position` on the board.
 *
 * ⚠️ NOT PAGED, for {@link ListTrackTasksResponseSchema}'s reason: a document
 * outline only means anything whole, because `parentSectionId` has to resolve
 * inside the answer and `position` is unique per PARENT rather than per track.
 *
 * 🔴 A FOREIGN OR ABSENT `trackId` IS REFUSED WITH 404 — **identically**, and
 * that indistinguishability is the security property rather than an accident.
 * `TrackAccessGuard` runs on every `:trackId` route and calls
 * `TrackAccessService.assertCanRead`, whose `canReach` reads the track through
 * `(id, organizationId)` together: another organisation's row and a row that
 * never existed both come back `null`, and both raise `TrackNotFoundError`. So
 * the caller cannot learn whether another tenant's id exists.
 *
 * ⚠️ THE USE CASE BEHIND THIS ROUTE REALLY DOES ANSWER `[]` FOR A FOREIGN
 * TRACK, AND THAT IS WHY THIS IS EASY TO GET WRONG. `ListTrackSectionsUseCase`
 * runs no existence check by design — but no caller reaches it, because the
 * guard refuses first. Read the ROUTE's answer here, never the use case's.
 */
export const ListTrackSectionsResponseSchema = z.object({
  sections: z.array(TrackSectionSchema)
});
export type ListTrackSectionsResponse = z.infer<typeof ListTrackSectionsResponseSchema>;

/** A section slug is the addressable half of the path, so it is a slug. */
const TrackSectionSlugSchema = z
  .string()
  .trim()
  .regex(/^[a-z0-9][a-z0-9-]{0,63}$/, "A section slug is 1-64 chars of [a-z0-9-].");

export const CreateTrackSectionBodySchema = z
  .object({
    /** `null` creates at the ROOT level of the track. */
    parentSectionId: z.string().uuid().nullish(),
    slug: TrackSectionSlugSchema,
    title: z.string().trim().min(1),
    body: z.string().optional(),
    /** Where among its siblings. Omitted appends; out of range clamps. */
    position: z.number().int().min(0).optional()
  })
  .strict();
export type CreateTrackSectionBody = z.infer<typeof CreateTrackSectionBodySchema>;

export const RenameTrackSectionBodySchema = z
  .object({
    newSlug: TrackSectionSlugSchema
  })
  .strict();
export type RenameTrackSectionBody = z.infer<typeof RenameTrackSectionBodySchema>;

export const RenameTrackSectionResponseSchema = z.object({
  oldPath: z.string(),
  newPath: z.string(),
  /**
   * The renamed node plus every descendant. `1` when it is a leaf, and `0` when
   * the new slug equals the old one — that rename is a no-op and writes nothing,
   * so a caller must not read `0` as "the section vanished".
   */
  rowsRewritten: z.number().int()
});
export type RenameTrackSectionResponse = z.infer<typeof RenameTrackSectionResponseSchema>;

// ═══════════════════════════════════════════════════════════════
// TASKS
// ═══════════════════════════════════════════════════════════════

/**
 * A task, with its banner FIRST.
 *
 * 🔴 THE FIELD ORDER IS THE CONTRACT, NOT THE STYLE. An agent reads a payload
 * top-down and acts on what it sees first, so a banner in the middle is a banner
 * that arrives after the decision it was meant to inform. `JSON.stringify`
 * preserves the insertion order of string keys, so declaring `banner` first is
 * what puts it first on the wire.
 */
export const TrackTaskSchema = z.object({
  banner: z.string(),
  id: z.string().uuid(),
  trackId: z.string().uuid(),
  parentTaskId: z.string().uuid().nullable(),
  /**
   * Where this task sits among its siblings, from 0.
   *
   * 🔴 IT IS PUBLISHED BECAUSE IT IS THE ONLY THING THAT ORDERS THE TREE, AND A
   * CONSUMER WITHOUT IT ORDERS BY ARRAY ARRIVAL WITHOUT KNOWING IT IS DOING SO.
   * `@@unique([trackId, parentTaskId, position])` makes it a total order within
   * a parent, and the plan import writes it deliberately — so a client that
   * renders the tree in whatever sequence a page happened to arrive in shows a
   * plan whose steps are in the wrong order, silently and plausibly.
   *
   * ⚠️ IT IS UNIQUE PER PARENT, NOT PER TRACK. Two tasks under different parents
   * legitimately share a position; sorting a flat list by this column alone
   * interleaves the branches. Group by `parentTaskId` first.
   */
  position: z.number().int(),
  /**
   * What this row IS.
   *
   * 🔴 `STEP` IS WORK AND IS THE ONLY KIND THE ROLL-UP COUNTS. `DECISION` and
   * `DEFINITION` are content filed on the board — a choice that was taken, a
   * rule or an axis that was settled — and they are outstanding work in neither
   * direction: they are absent from `done`, absent from `total`, and absent from
   * the ready set. Before this field existed every imported plan entry was a
   * step by default, and a plan whose entries were half prose reported a
   * denominator nobody would ever close.
   *
   * ⚠️ EVERY ROW WRITTEN BEFORE THIS COLUMN EXISTED READS `STEP`, and so does
   * every row a caller creates without naming a kind. The column is
   * `NOT NULL DEFAULT 'STEP'`, so this is a fact about the database rather than
   * a convention a reader has to trust.
   */
  kind: TrackTaskKindSchema,
  title: z.string(),
  /**
   * The curated short name, or `null`. See {@link CreateTrackResponseSchema}.
   */
  shortTitle: z.string().nullable(),
  acceptance: z.string().nullable(),
  /** `true` when ticking this task requires evidence. */
  gate: z.boolean(),
  evidence: z.string().nullable(),
  /** ISO-8601, or `null` while the task is open. */
  doneAt: z.string().nullable(),
  /**
   * Who ticked it, or `null`.
   *
   * 🔴 IT IS A `User.id` AND IT IS NOT A UUID COLUMN. `TrackTask.doneByUserId` is
   * a bare `String` in the schema, like every other `userId` this product stores,
   * so uuid-checking it here would turn one legacy row into a failed task read.
   * The column, not the intent — the same rule
   * {@link StoredAssignmentEntityIdSchema} states for the folder assignments.
   *
   * ⚠️ IT IS `null` FOR EVERY MACHINE CALLER, AND THAT IS NOT A BUG TO BACKFILL
   * AROUND. The tick is reachable with an org API key that resolves no owning
   * human, and this domain writes `null` rather than a fabricated author. So
   * "nobody is attributed" and "an agent did it" are the same value here; do not
   * read an absence as a person who is missing.
   *
   * It is published because attribution that never reaches a client is
   * attribution nobody has: the column has been written since the domain shipped
   * and no route has ever returned it, so a surface asking "who closed this"
   * could not answer even for the ticks a person made.
   */
  doneByUserId: z.string().nullable(),
  /**
   * Whose turn it is on this row: `CUE`, `USER` or `EVENT`.
   *
   * 🔴 IT IS PUBLISHED HERE BECAUSE THE TICKET'S OWN REPRODUCTION IS THAT IT WAS
   * NOT (NEX-4713, step 2): "`task get` shows title, acceptance, gate,
   * claimedByAgentId, doneAt. There is no owner." The one route that shows a
   * single row whole could not answer why that row was in `task ready`'s
   * `waiting` half rather than its `workable` one.
   *
   * ⚠️ IT IS NONE OF THE THREE PERSON-SHAPED FIELDS BESIDE IT, AND CONFUSING THEM
   * IS HOW A PERMISSION CHECK GETS WRITTEN AGAINST IT. Not the org RBAC grant
   * (`ResourcePermission` + `Relation.OWNER`) — this grants and refuses nothing,
   * so never gate access on it. Not `claimedByAgentId` below, which is
   * COORDINATION: which agent said it is on this row, right now. Not
   * `doneByUserId` above, which is ATTRIBUTION after the fact. This is a standing
   * statement about which KIND of actor is expected to act next, and it is set
   * whether or not anybody has claimed anything.
   *
   * ⚠️ EVERY ROW WRITTEN BEFORE THE COLUMN EXISTED READS `CUE`, and so does every
   * plan entry that does not name one. The column is `NOT NULL DEFAULT 'CUE'`, so
   * that is a fact about the database rather than a convention to trust.
   */
  nextOwner: TrackNextOwnerSchema,
  claimedByAgentId: z.string().uuid().nullable(),
  /**
   * ISO-8601. When this row joined the plan.
   *
   * 🔴 IT IS PUBLISHED SO A CALLER CAN EXPLAIN A DENOMINATOR THAT MOVED
   * (NEX-4543). A roll-up is a snapshot, so a plan that GREW while work was
   * being done reads as work going backwards: `5/20 → 6/20 → 6/36 → 9/136` is
   * three tasks closed and 116 added, and every published number was correct
   * while the board looked frozen. With this beside `doneAt`, "closed in this
   * window" and "added in this window" are both a filter over the answer this
   * route already gives, and neither is a new number anybody has to keep honest.
   *
   * ⚠️ IT REDEFINES NOTHING. `done`, `total` and `byKind` mean exactly what they
   * meant before this field existed; the column is `DEFAULT now()` and has been
   * written since the domain shipped, so every task already has one. That is the
   * whole reason this is the answer and a windowed closed-count is not: an
   * aggregate would be a fourth published number over the same rows, and this
   * domain has spent two tickets on the two it already has.
   *
   * ⚠️ IT IS THE ROW'S BIRTH, NOT THE PLAN'S. A plan imported in one call gives
   * every task the same instant, so this separates ADDED-LATER from
   * ADDED-AT-IMPORT and cannot order what arrived together. Nothing records who
   * added a task or why.
   */
  createdAt: z.string()
});
export type TrackTask = z.infer<typeof TrackTaskSchema>;

/**
 * A track's WHOLE plan — every task, at every depth, in one answer.
 *
 * 🔴 IT IS NOT PAGED, AND THAT IS THE SAME ARGUMENT THE ROLL-UP MAKES. The tree
 * only means anything whole: `parentTaskId` resolves inside the answer, and
 * `position` is unique per PARENT rather than per track, so a page hands a
 * caller a forest of orphans it cannot re-root and an order it cannot restore. A
 * bounded read of this resource would have to be bounded by SUBTREE, which is a
 * different question with a different input.
 *
 * ⚠️ GROUPED BY `parentTaskId`, THEN IN `position` ORDER. Sorting the flat array
 * by `position` alone interleaves the branches, because two tasks under
 * different parents legitimately share a position.
 *
 * 🔴 AN EMPTY LIST MEANS A REAL, READABLE TRACK WITH NO TASKS. A track id this
 * caller cannot reach is a 404 from `TrackAccessGuard`, which names a `:trackId`
 * and so runs before the handler on both transports — and its refusal is ONE
 * answer for an absent id, another organisation's and an ungranted one, so the
 * status code discriminates *unreachable* without ever discriminating *foreign*
 * from *absent*. {@link TrackRollupSchema} and
 * {@link ListTrackSectionsResponseSchema} carry the same note for the same
 * reason.
 *
 * ⚠️ AND `GET /tracks/:trackId/rollup` IS NOT THE COUNTER-EXAMPLE IT READS AS.
 * The BATCH route `GET /tracks/rollup` answers `0/0` for an id the caller may
 * not open, because it takes its ids in a QUERY and is declared
 * `narrowed-by-the-use-case`; the per-track `GET /tracks/:trackId/rollup` names
 * `:trackId` on the PATH, so `TrackAccessGuard` decides it and it 404s like
 * every other `:trackId` route. Two rollup routes, two different answers, and
 * only the batch one is `0/0`.
 *
 * ⚠️ IT CARRIES EVERY KIND, INCLUDING THE ONES THE ROLL-UP DOES NOT COUNT. This
 * is the board, not the burndown: a `DECISION` and a `DEFINITION` are the record
 * somebody filed and are exactly what an audit came here to read. Filter on
 * {@link TrackTaskSchema}'s `kind` if you want only the work.
 */
export const ListTrackTasksResponseSchema = z.object({
  tasks: z.array(TrackTaskSchema)
});
export type ListTrackTasksResponse = z.infer<typeof ListTrackTasksResponseSchema>;

/**
 * 🔴 A CLAIM REFUSES NOTHING AND TAKES NO LOCK. Claiming a task another agent
 * holds SUCCEEDS and overwrites, because `claimedByAgentId` is coordination and
 * not access control: there is one credential per organisation and no per-agent
 * identity, so a refusal would enforce nothing and would have to be recovered
 * from. Claiming and taking over are one operation, which is why the contract
 * declares no separate take-over route.
 *
 * 🔴 `agentId` TAKES AN ID OR A NAME, AND IT IS THE REQUEST FIELD ONLY.
 * `ClaimTrackTaskResponseSchema.claimedByAgentId` below stays `.uuid()` and the
 * route returns the RESOLVED id, never the value it was sent — so a name is a
 * spelling the caller may use and never a value this surface hands back.
 *
 * The name is unique among a track's OPEN agents (`TrackAgent_open_name_unique`,
 * a PARTIAL index this schema's siblings document), and it is the value a caller
 * already has: the collision banner every task read carries names the HOLDER by
 * name so a person can recognise it. Requiring the uuid meant reading the banner,
 * then running a second command to translate a name it had just printed.
 *
 * ⚠️ THE ID WINS WHEN A VALUE COULD BE EITHER, exactly as `TrackRefSchema` above
 * resolves a track. `OpenTrackAgentBodySchema.name` carries no character class, so
 * every canonical uuid is a legal agent name — the two spellings genuinely
 * overlap and something has to break the tie. The published consequence: an agent
 * NAMED after a uuid is claimable by its own id and NOT by that name. It costs
 * nothing across a tenancy boundary — the lookup is scoped to the caller's
 * organisation and to the task's own track, behind that track's write gate — and
 * it is the only stable assignment, because the id is the form the server mints
 * and the name is the form a caller chooses.
 *
 * ⚠️ ONE `ZodString`, DELIBERATELY, AND A `z.union` IS NOT EQUIVALENT — the same
 * reason {@link TRACK_REF_PATTERN} states. `packages/cli/src/commands/tracks.contract.generated.ts`
 * records this field as `type: "string"`; a union reaches that generator as a
 * different node and would rewrite the row for a wire shape that has not moved.
 * There is no regex here because there is nothing to constrain: the bound is the
 * agent NAME's own — `.trim().min(1).max(128)`, matching
 * `OpenTrackAgentBodySchema.name` — and every uuid is inside it.
 */
export const ClaimTrackTaskBodySchema = z
  .object({
    /** An OPEN agent on the same track, by `id` or by `name`. The id wins. */
    agentId: z.string().trim().min(1).max(128)
  })
  .strict();
export type ClaimTrackTaskBody = z.infer<typeof ClaimTrackTaskBodySchema>;

export const ClaimTrackTaskResponseSchema = z.object({
  taskId: z.string().uuid(),
  claimedByAgentId: z.string().uuid()
});
export type ClaimTrackTaskResponse = z.infer<typeof ClaimTrackTaskResponseSchema>;

export const ToggleTrackTaskBodySchema = z
  .object({
    /** `true` ticks it, `false` un-ticks it. */
    done: z.boolean(),
    /** Required when the task itself is a gate. Ignored on an un-tick. */
    evidence: z.string().trim().min(1).nullish()
  })
  .strict();
export type ToggleTrackTaskBody = z.infer<typeof ToggleTrackTaskBodySchema>;

export const ToggleTrackTaskResponseSchema = z.object({
  taskId: z.string().uuid(),
  done: z.boolean()
});
export type ToggleTrackTaskResponse = z.infer<typeof ToggleTrackTaskResponseSchema>;

export const CreateTrackTaskEdgeBodySchema = z
  .object({
    /** The task that must finish first. */
    blockerTaskId: z.string().uuid(),
    /** The task that waits. */
    blockedTaskId: z.string().uuid()
  })
  .strict();
export type CreateTrackTaskEdgeBody = z.infer<typeof CreateTrackTaskEdgeBodySchema>;

export const CreateTrackDependencyEdgeBodySchema = z
  .object({
    /** The track that must finish first. */
    blockerTrackId: z.string().uuid(),
    /** The track that waits. */
    blockedTrackId: z.string().uuid()
  })
  .strict();
export type CreateTrackDependencyEdgeBody = z.infer<typeof CreateTrackDependencyEdgeBodySchema>;

/**
 * Both edge routes answer with the id of the row they inserted.
 *
 * ⚠️ THE FIELD IS `id`, NOT `edgeId`, AND THAT IS FORCED BY A GATE RATHER THAN
 * CHOSEN. `id-validation-consistency.test.ts` treats an id NAME as uuid-checked
 * once any v1 schema checks it, and then requires every request schema carrying
 * that name to check it too. `edgeId` is already spoken for by the workflow
 * graph, where an edge id comes from whatever drew it — `crypto.randomUUID()`
 * from the canvas, `nanoid()` on duplicate, React Flow's own
 * `xy-edge__<source>-<target>` on a dragged connection — so uuid-checking the
 * name here would have reddened `WorkflowEdgeIdParamSchema`, a file this change
 * never touched, and made every edge drawn in the product unaddressable.
 */
export const TrackEdgeCreatedResponseSchema = z.object({
  id: z.string().uuid()
});
export type TrackEdgeCreatedResponse = z.infer<typeof TrackEdgeCreatedResponseSchema>;

/**
 * One task edge as a READER sees it: the row's own id, and the two ends.
 *
 * 🔴 `id` IS WHAT SEPARATES THIS FROM THE GRAPH VOCABULARY, AND IT IS THE WHOLE
 * REASON A NEW PORT EXISTS. `TrackGraphEdge` (`{scopeId, fromId, toId}`) is what
 * the cycle walk consumes, and a walk addresses NODES — it never needs the row.
 * A surface that DRAWS the graph needs a stable key per edge, and the delete
 * route this domain will grow needs something to address. Reading the edges
 * through the walk's vocabulary would leave both unbuildable.
 *
 * ⚠️ `blockerTaskId` FINISHES FIRST; `blockedTaskId` WAITS. The names are the
 * same way round as {@link CreateTrackTaskEdgeBodySchema}, deliberately, so a
 * caller can round-trip what it reads without inverting anything.
 *
 * ⚠️ NO CYCLE INFORMATION, EVER. Refusing a circle is the WRITE path's job and
 * it happens inside an advisory lock, over a snapshot this read does not have.
 * A second walk computed here would be a second opinion over a different
 * snapshot, which is exactly the drift `find-track-graph-cycles` is called from
 * one place to prevent.
 */
export const TrackTaskEdgeSchema = z.object({
  id: z.string().uuid(),
  /** The task that must finish first. */
  blockerTaskId: z.string().uuid(),
  /** The task that waits. */
  blockedTaskId: z.string().uuid()
});
export type TrackTaskEdge = z.infer<typeof TrackTaskEdgeSchema>;

/**
 * What blocks what, inside one track's plan.
 *
 * 🔴 THIS IS THE OTHER HALF OF A TASK LIST AND NEITHER IS USEFUL ALONE.
 * {@link ListTrackTasksResponseSchema} says which rows are open;
 * `TrackListReadyTasks` says which of them can be picked up. Nothing said WHY
 * the rest could not — so an open task that is neither done nor ready was
 * unexplainable from this surface, in exactly the shape a progress denominator
 * takes when a reader can see the count and not decompose it.
 *
 * ⚠️ UNORDERED. Nothing in this schema can rank two edges: `TrackTaskEdge` has
 * no position and the table has no ordering column, so promising an order here
 * would be a promise the storage cannot keep.
 *
 * 🔴 A FOREIGN OR ABSENT `trackId` IS REFUSED WITH 404, identically — the same
 * guard and the same reasoning {@link ListTrackSectionsResponseSchema} records,
 * including why the use case's own `[]` never reaches a caller.
 */
export const ListTrackTaskEdgesResponseSchema = z.object({
  edges: z.array(TrackTaskEdgeSchema)
});
export type ListTrackTaskEdgesResponse = z.infer<typeof ListTrackTaskEdgesResponseSchema>;

// ═══════════════════════════════════════════════════════════════
// PLAN IMPORT
// ═══════════════════════════════════════════════════════════════

/**
 * One task as the plan author wrote it.
 *
 * ⚠️ THE NESTING IS DECLARED WITH AN EXPLICIT DEPTH RATHER THAN A `z.lazy()`
 * RECURSION, and that is a refusal rather than a limitation of Zod. A plan
 * arrives over HTTP, so its depth is caller-controlled: an unbounded recursive
 * schema turns a deep plan into a stack overflow inside the parser, before any
 * CHECK, any transaction or any refusal this contract could express. Four levels
 * is what the domain's own flattening was written against, and a plan deeper
 * than that is imported as several plans.
 */
const TrackPlanLeafSchema = z
  .object({
    title: z.string().trim().min(1),
    /**
     * A hand-written short name for this task, at most five words. `null` or
     * omitted leaves it uncurated and a reader falls back to `title`.
     *
     * 🔴 THIS IS THE ONLY DOOR A TASK'S `shortTitle` CAN ARRIVE THROUGH, for the
     * reason `kind` states one field down: the plan import is the only way a task
     * row is born, and there is no single-task create and no task update on
     * either surface. A plan whose entries do not name one imports a plan of
     * uncurated tasks, which is exactly today's meaning.
     *
     * Bounded by {@link TrackShortTitleSchema} — words here, characters here AND
     * in `Track_task_short_title_bounded`.
     */
    shortTitle: TrackShortTitleSchema.nullish(),
    /**
     * One line of acceptance, capped at {@link TRACK_ONE_LINE_MAX_CHARACTERS}.
     *
     * `Track_task_acceptance_bounded` is `Track_currentStep_bounded`'s twin —
     * same unit, same number, same 23514-into-a-500 — so it is refused by the
     * same {@link TrackOneLineSchema} rather than by a second spelling of it.
     */
    acceptance: TrackOneLineSchema.nullish(),
    /** A gate blocks its whole ancestry from being ticked until it carries evidence. */
    gate: z.boolean().optional(),
    /**
     * What this entry IS. `STEP` when omitted.
     *
     * 🔴 THIS IS THE ONLY DOOR THE KIND CAN ARRIVE THROUGH, because the plan
     * import is the only way a task row is born — there is no single-task create
     * route anywhere on either surface. A plan whose prose entries do not name a
     * kind here becomes a plan of steps, which is exactly the defect this field
     * exists to fix, so an importer that knows the difference must say so at the
     * import.
     *
     * ⚠️ OPTIONAL RATHER THAN REQUIRED, DELIBERATELY. Making it required would
     * refuse every plan written against the previous contract — including the
     * ones already in flight — to gain nothing the column default does not
     * already give.
     */
    kind: TrackTaskKindSchema.optional(),
    /**
     * Whose turn this entry is. `CUE` when omitted.
     *
     * 🔴 THIS IS THE ONLY DOOR `nextOwner` CAN ARRIVE THROUGH, for the reason
     * `kind` and `shortTitle` state above: the plan import is the only way a task
     * row is born, and there is no single-task create and no task update on either
     * surface. Without this field the column added by the first half of NEX-4713
     * is unwritable, every row stays `CUE` forever, and `task ready`'s `waiting`
     * half can never be non-empty in production — a split nobody could reach.
     *
     * The ticket's own reproduction begins here: "`nexus tracks plan import
     * <trackId>` with a row that only the owner can settle, such as a visual check
     * or a product decision." This is where the author says so.
     *
     * ⚠️ OPTIONAL RATHER THAN REQUIRED, DELIBERATELY, and for `kind`'s reason: a
     * required field would refuse every plan written against the previous
     * contract — including the ones already in flight — to gain nothing the column
     * default does not already give.
     *
     * ⚠️ AN OWNER DOES NOT PROPAGATE TO CHILDREN. A `USER` parent whose sub-steps
     * are ordinary agent work is the common shape — "decide the copy" over "wire
     * the three strings" — and inheriting would park that whole subtree on a
     * person who was only asked about the parent.
     */
    nextOwner: TrackNextOwnerSchema.optional()
  })
  .strict();

const TrackPlanDepth3Schema = TrackPlanLeafSchema.extend({
  children: z.array(TrackPlanLeafSchema).optional()
}).strict();
const TrackPlanDepth2Schema = TrackPlanLeafSchema.extend({
  children: z.array(TrackPlanDepth3Schema).optional()
}).strict();
export const TrackPlanNodeSchema = TrackPlanLeafSchema.extend({
  children: z.array(TrackPlanDepth2Schema).optional()
}).strict();
export type TrackPlanNode = z.infer<typeof TrackPlanNodeSchema>;

/**
 * One dependency, by INDEX into the plan's depth-first pre-order flattening.
 *
 * 🔴 THE FLATTENING ORDER IS THE COORDINATE SYSTEM, AND GETTING IT WRONG FAILS
 * SILENTLY. A plan's edges name their endpoints by index because a plan is
 * authored before any of its tasks has an id, so the order is the contract:
 * shift one entry and every edge still inserts, every row count still matches,
 * and the dependencies land on the wrong tasks with no error to read. The order
 * is a node, then its whole subtree, then its next sibling —
 * `[A [A1, A2 [A2a]], B]` numbers `0:A 1:A1 2:A2 3:A2a 4:B`. Breadth-first is
 * what a reader implements by accident when they reach for a queue.
 */
export const TrackPlanEdgeSchema = z
  .object({
    blockerIndex: z.number().int().min(0),
    blockedIndex: z.number().int().min(0)
  })
  .strict();
export type TrackPlanEdge = z.infer<typeof TrackPlanEdgeSchema>;

export const ImportTrackPlanBodySchema = z
  .object({
    /** Hang the plan's roots under this task, or under the track when omitted. */
    parentTaskId: z.string().uuid().nullish(),
    tasks: z.array(TrackPlanNodeSchema).min(1),
    edges: z.array(TrackPlanEdgeSchema).optional()
  })
  .strict();
export type ImportTrackPlanBody = z.infer<typeof ImportTrackPlanBodySchema>;

export const ImportTrackPlanResponseSchema = z.object({
  /** One id per entry of the depth-first flattening, in that order. */
  taskIdsByIndex: z.array(z.string().uuid()),
  edgeIds: z.array(z.string().uuid())
});
export type ImportTrackPlanResponse = z.infer<typeof ImportTrackPlanResponseSchema>;

// ═══════════════════════════════════════════════════════════════
// AGENTS
// ═══════════════════════════════════════════════════════════════

export const TrackAgentSchema = z.object({
  id: z.string().uuid(),
  trackId: z.string().uuid(),
  name: z.string(),
  state: TrackAgentStateSchema,
  /** Other agents' NAMES in this track — a column, not a join. */
  dependsOn: z.array(z.string()),
  acceptance: z.string().nullable(),
  /** Opaque by design: what a dispatcher handed the agent. Never `z.any()`. */
  inputs: z.unknown().nullable(),
  outputPath: z.string().nullable(),
  model: z.string().nullable(),
  reason: z.string().nullable(),
  openedAt: z.string(),
  /**
   * The ONE heartbeat clock in this domain. The collision banner's staleness is
   * derived from it and from nothing else.
   */
  lastHeardAt: z.string(),
  closedAt: z.string().nullable()
});
export type TrackAgent = z.infer<typeof TrackAgentSchema>;

export const ListTrackAgentsQuerySchema = z.object({
  state: TrackAgentStateSchema.optional()
});
export type ListTrackAgentsQuery = z.infer<typeof ListTrackAgentsQuerySchema>;

export const ListTrackAgentsResponseSchema = z.object({
  agents: z.array(TrackAgentSchema)
});
export type ListTrackAgentsResponse = z.infer<typeof ListTrackAgentsResponseSchema>;

/**
 * 🔴 THE NAME IS UNIQUE AMONG **OPEN** AGENTS ONLY, VIA A PARTIAL INDEX PRISMA
 * CANNOT SEE. `TrackAgent_open_name_unique` lives in the migration alone, so
 * `prisma migrate diff` will never propose dropping it and will never report it
 * as drift. Closing an agent frees its name, and re-opening it is a 409 until
 * then.
 */
export const OpenTrackAgentBodySchema = z
  .object({
    name: z.string().trim().min(1).max(128),
    dependsOn: z.array(z.string().trim().min(1)).default([]),
    acceptance: z.string().trim().min(1).nullish(),
    /**
     * ⚠️ `z.record(z.string(), z.unknown())` RATHER THAN `z.unknown()`, AND THE
     * REASON IS A GATE. `z.unknown().nullish()` converts to
     * `{"anyOf":[{},{"type":"null"}]}` — a field an MCP client is offered with
     * NOTHING said about it — and
     * `public/v1/mcp-inbound/__tests__/contract-input-schema-fidelity.spec.ts` refuses
     * that shape by name. An open object is what a dispatcher actually hands an
     * agent, so declaring it is the honest fix rather than the quiet one; the
     * VALUES stay unconstrained.
     */
    inputs: z.record(z.string(), z.unknown()).nullish(),
    outputPath: z.string().trim().min(1).nullish(),
    model: z.string().trim().min(1).nullish()
  })
  .strict();
export type OpenTrackAgentBody = z.infer<typeof OpenTrackAgentBodySchema>;

export const CloseTrackAgentBodySchema = z
  .object({
    state: TrackAgentCloseStateSchema,
    /**
     * Mandatory in practice for `DEAD` and `RETIRED`, and NOT expressed here as a
     * refinement.
     *
     * 🔴 THE RULE IS THE CHECK CONSTRAINT'S OWN PREDICATE —
     * `char_length(btrim(translate(reason, …))) >= 15`. Re-implementing it here
     * would put a THIRD statement of it in the tree: the CHECK, the domain value
     * object that mirrors it, and this. The copy that stops matching is the one
     * that reports a clean parse over a row the database refuses, so the use case
     * owns the refusal, using the one mirror.
     */
    reason: z.string().nullish()
  })
  .strict();
export type CloseTrackAgentBody = z.infer<typeof CloseTrackAgentBodySchema>;

// ═══════════════════════════════════════════════════════════════
// DIARY
// ═══════════════════════════════════════════════════════════════

export const TrackDiaryEntrySchema = z.object({
  id: z.string().uuid(),
  trackId: z.string().uuid(),
  kind: TrackDiaryKindSchema,
  body: z.string(),
  taskId: z.string().uuid().nullable(),
  agentId: z.string().uuid().nullable(),
  workspaceId: z.string().uuid().nullable(),
  artifactPath: z.string().nullable(),
  /** A User id, which is NOT a UUID in this database. */
  authorUserId: z.string().nullable(),
  createdAt: z.string()
});
export type TrackDiaryEntry = z.infer<typeof TrackDiaryEntrySchema>;

/**
 * ⚠️ `limit` CARRIES A `.default()` HERE AND NOT ON THE READY SET, AND THE
 * DIFFERENCE IS THE PORT'S, NOT A STYLE CHOICE. `ListTrackDiaryEntriesInput.limit`
 * is a REQUIRED `number`, so the contract is the only place a default can come
 * from; `ListReadyTracksQuery.limit` is optional and `clampReadySetLimit` owns its
 * default, and declaring a second one here would be a copy that drifts.
 */
export const ListTrackDiaryEntriesQuerySchema = z.object({
  kind: TrackDiaryKindSchema.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50)
});
export type ListTrackDiaryEntriesQuery = z.infer<typeof ListTrackDiaryEntriesQuerySchema>;

export const ListTrackDiaryEntriesResponseSchema = z.object({
  entries: z.array(TrackDiaryEntrySchema)
});
export type ListTrackDiaryEntriesResponse = z.infer<typeof ListTrackDiaryEntriesResponseSchema>;

/**
 * 🔴 THIS IS THE DIARY'S ONLY WRITE, AND THE ABSENCE OF THE OTHER TWO IS THE
 * GUARANTEE. The contract declares no update and no delete for this resource —
 * not a guarded one, an ABSENT one — because a wrong entry is superseded by a
 * later entry. That is what makes the log admissible as a record of what
 * happened rather than of what somebody last decided should have happened, and
 * `apps/backend/src/__governance__/tracks-diary-is-append-only.spec.ts` asserts
 * the absence off the live route table.
 */
export const AppendTrackDiaryEntryBodySchema = z
  .object({
    kind: TrackDiaryKindSchema,
    /**
     * `.trim()` BEFORE `.min(1)`. The other order measures the UNTRIMMED value, so
     * `"   "` passes — the house's most common spelling of this check, and it
     * fails OPEN wherever the value is later used as a substring.
     */
    body: z.string().trim().min(1),
    taskId: z.string().uuid().nullish(),
    agentId: z.string().uuid().nullish(),
    workspaceId: z.string().uuid().nullish(),
    artifactPath: z.string().trim().min(1).nullish()
  })
  .strict();
export type AppendTrackDiaryEntryBody = z.infer<typeof AppendTrackDiaryEntryBodySchema>;

// ═══════════════════════════════════════════════════════════════
// MEMORY
// ═══════════════════════════════════════════════════════════════

export const TrackMemoryEntrySchema = z.object({
  id: z.string().uuid(),
  trackId: z.string().uuid(),
  key: z.string(),
  value: z.string(),
  /** UTF-8 bytes, matching `octet_length()`. NOT characters and NOT code units. */
  valueBytes: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string()
});
export type TrackMemoryEntry = z.infer<typeof TrackMemoryEntrySchema>;

export const ListTrackMemoryEntriesResponseSchema = z.object({
  entries: z.array(TrackMemoryEntrySchema),
  /**
   * Summed from the rows THIS read returned rather than from `Track.memoryBytes`,
   * deliberately: the two are maintained by one transaction, so publishing the
   * row sum makes any divergence VISIBLE to a caller instead of hiding it behind
   * the counter that is supposed to track it.
   */
  trackMemoryBytes: z.number().int(),
  budgetBytes: z.number().int()
});
export type ListTrackMemoryEntriesResponse = z.infer<typeof ListTrackMemoryEntriesResponseSchema>;

export const PutTrackMemoryEntryBodySchema = z
  .object({
    key: TrackMemoryKeySchema,
    /**
     * NOT `.max(n)`. Zod counts UTF-16 code units, the CHECK counts BYTES, and the
     * two disagree by up to 3x on non-ASCII. `.min(1)` after `.trim()` refuses a
     * blank, which is the half a length gate genuinely does answer.
     */
    value: z.string().trim().min(1)
  })
  .strict();
export type PutTrackMemoryEntryBody = z.infer<typeof PutTrackMemoryEntryBodySchema>;

export const PutTrackMemoryEntryResponseSchema = z.object({
  entry: TrackMemoryEntrySchema,
  trackMemoryBytes: z.number().int()
});
export type PutTrackMemoryEntryResponse = z.infer<typeof PutTrackMemoryEntryResponseSchema>;

export const DeleteTrackMemoryEntryResponseSchema = z.object({
  /** Idempotent — `false` when the key was already absent. */
  deleted: z.boolean(),
  trackMemoryBytes: z.number().int()
});
export type DeleteTrackMemoryEntryResponse = z.infer<typeof DeleteTrackMemoryEntryResponseSchema>;

// ═══════════════════════════════════════════════════════════════
// EVENTS
// ═══════════════════════════════════════════════════════════════

export const TrackEventSchema = z.object({
  id: z.string().uuid(),
  /** `null` for an organisation-level event with no track. */
  trackId: z.string().uuid().nullable(),
  type: z.string(),
  payload: z.unknown().nullable(),
  /** A User id, which is NOT a UUID in this database. */
  actorUserId: z.string().nullable(),
  actorAgentId: z.string().uuid().nullable(),
  createdAt: z.string()
});
export type TrackEvent = z.infer<typeof TrackEventSchema>;

/** `limit` defaults here for the reason {@link ListTrackDiaryEntriesQuerySchema} gives. */
export const ListTrackEventsQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50)
});
export type ListTrackEventsQuery = z.infer<typeof ListTrackEventsQuerySchema>;

export const ListTrackEventsResponseSchema = z.object({
  events: z.array(TrackEventSchema)
});
export type ListTrackEventsResponse = z.infer<typeof ListTrackEventsResponseSchema>;

/**
 * THE EVENT-FEED CURSOR'S GRAMMAR, and it is DECLARED HERE — in the contract
 * package, beside the schema — for the reason {@link TrackListCursor}'s own header
 * gives about the track catalog's: the token is part of what the API PROMISES, so
 * one implementation serves the v1 surface, the internal surface and any future
 * one, rather than three that agree until they do not.
 *
 * ## `<createdAt>~<id>.<fingerprint>`
 *
 * The key is `(createdAt, id)` — the position of the last row a page delivered.
 * The pair rather than the timestamp alone because `TrackEvent.createdAt` is
 * `TIMESTAMP(3)` and same-millisecond ties are ordinary: a cursor on the timestamp
 * alone must choose between `<`, which DROPS every tied row it has not yet
 * delivered, and `<=`, which RE-DELIVERS the ones it has. `id` is a `uuid` column,
 * ordered by Postgres on its byte value with no collation involved, so it makes the
 * order total and neither choice has to be made.
 *
 * ⚠️ THE TRACK CATALOG'S CURSOR NEEDS NO TIEBREAKER AND THIS ONE DOES. That one
 * pages on `number`, which `@@unique([organizationId, number])` already makes a
 * total order inside an organisation. Events have no such column, and they arrive
 * in bursts that tie. The two are the same SHAPE deliberately; do not read the
 * catalog's single key as evidence that a single key is enough here.
 *
 * ## The fingerprint, and why the token carries one
 *
 * A cursor is a position INSIDE A FILTERED SET. Round-tripping one from a
 * `type=build.failed` page into an unfiltered read returns a correctly-scoped,
 * correctly-ordered page that STARTS IN THE MIDDLE — a wrong answer with nothing
 * to raise. So the token carries the filters it was issued under and the server
 * refuses a mismatch. `limit` is deliberately NOT in it: changing the page size
 * mid-walk changes how many rows arrive, never which.
 *
 * 🔴 FNV-1a RATHER THAN A READABLE FILTER LIST, AND NOT FOR SECRECY. The catalog's
 * fingerprint can be readable because its filters are enums; `type` here is free
 * text a caller supplies, so a readable fingerprint would need the separators
 * escaped out of an arbitrary string and the grammar would carry that escaping
 * forever. A collision means ONE token accepted that should have been refused, on a
 * caller that changed filters mid-walk — which is why a non-cryptographic hash is
 * the right instrument and `node:crypto` (unbundleable in the frontend, which
 * imports this package) is not.
 */
const TRACK_EVENT_CURSOR =
  /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z)~([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})\.([0-9a-f]{8})$/;

/** The filters a feed page was read under, as they appear inside a cursor. */
export interface TrackEventFeedFilters {
  /** The raw ISO string the caller sent, never a re-serialised `Date`. */
  readonly since?: string;
  readonly type?: string;
}

/**
 * The filters, as one 8-hex-character fingerprint.
 *
 * `*` for an absent filter rather than the empty string, on the catalog cursor's
 * argument: an absent filter and one somebody cleared have to render identically,
 * and an empty segment is how two different filter sets fingerprint the same.
 *
 * 🔴 THE SEPARATOR IS NUL, AND IT IS WRITTEN AS AN ESCAPE RATHER THAN AS A LITERAL
 * BYTE. `type` is free text a caller supplies, so ANY printable separator is a value
 * `type` could itself contain — and then `{since:"a", type:"b"}` and the single
 * filter `"a<sep>b"` fingerprint identically, which silently accepts a cursor issued
 * for a different filter set. NUL is the one character a caller cannot send.
 *
 * ⚠️ WRITTEN LITERALLY IT IS UNREVIEWABLE AND UNEDITABLE: invisible in an editor,
 * rendered as a SPACE by the tooling that displays files, unmatchable by a
 * string-replacing editor, and refused by name by `.husky/pre-commit`. This exact
 * line arrived as a literal NUL once and was repaired with
 * `.githooks/repair-bytes.py --expect-hex 00 --replace-hex 5c7530303030`, which
 * proves the result from disk in bytes because reading it cannot.
 */
export const trackEventFeedFingerprint = (filters: TrackEventFeedFilters): string => {
  const canonical = `${filters.since ?? "*"}\u0000${filters.type ?? "*"}`;
  // FNV-1a, 32-bit. `Math.imul` keeps the multiply in 32 bits — a plain `*`
  // overflows into a double and silently stops being FNV after a few characters.
  let hash = 0x811c9dc5;
  for (let i = 0; i < canonical.length; i += 1) {
    hash ^= canonical.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
};

/** The position of one delivered row, under the filters it was read with. */
export const encodeTrackEventCursor = (
  row: { readonly createdAt: Date; readonly id: string },
  filters: TrackEventFeedFilters
): string => `${row.createdAt.toISOString()}~${row.id}.${trackEventFeedFingerprint(filters)}`;

export type TrackEventCursorRead =
  | { readonly ok: true; readonly createdAt: Date; readonly id: string }
  | { readonly ok: false; readonly reason: "malformed" | "filters-changed" };

/**
 * Read back a cursor this server issued, under the filters it is being replayed
 * with.
 *
 * 🔴 THE REGEX PINS THE CANONICAL MILLISECOND FORM, AND THAT IS THE WHOLE
 * TIMESTAMP CHECK. `new Date("2")` is a VALID Date in V8 — the first of February
 * 2001 — so a decoder that only asks `Number.isNaN(date.getTime())` accepts a token
 * whose position is years from anything the caller meant, and the page it returns
 * is a well-formed page of real events with nothing to notice. Millisecond
 * precision is also exactly what the column stores, so the round trip is lossless.
 */
export const decodeTrackEventCursor = (
  token: string,
  filters: TrackEventFeedFilters
): TrackEventCursorRead => {
  const match = TRACK_EVENT_CURSOR.exec(token);
  if (match === null) return { ok: false, reason: "malformed" };

  const [, iso, id, fingerprint] = match;
  if (iso === undefined || id === undefined || fingerprint === undefined) {
    return { ok: false, reason: "malformed" };
  }

  if (fingerprint !== trackEventFeedFingerprint(filters)) {
    return { ok: false, reason: "filters-changed" };
  }

  // The regex admits `2026-02-31T…`, which is a shape rather than a date. `Date`
  // rolls it forward to March, so the round trip is what refuses it.
  const createdAt = new Date(iso);
  if (Number.isNaN(createdAt.getTime()) || createdAt.toISOString() !== iso) {
    return { ok: false, reason: "malformed" };
  }

  return { ok: true, createdAt, id };
};

/**
 * The cursor for the NEXT page, or `null` when this page ends the stream.
 *
 * A short page means the query ran out of rows, so there is no next position to
 * name. A FULL page yields one even when it happened to land on the last row: "full"
 * and "full and final" are indistinguishable without counting, and counting an
 * append-only stream is a second scan for a number that is stale before it is sent.
 */
export const nextTrackEventCursor = (
  page: readonly { readonly createdAt: Date; readonly id: string }[],
  limit: number,
  filters: TrackEventFeedFilters
): string | null => {
  // Indexed rather than `Array.prototype.at`: this package's `lib` predates it, so
  // `.at(-1)` typechecks in `apps/backend` and fails here — the same expression,
  // two answers, decided by which tsconfig is reading it.
  const last = page.length === 0 ? undefined : page[page.length - 1];
  if (last === undefined || page.length < limit) return null;
  return encodeTrackEventCursor(last, filters);
};

/**
 * A page boundary in the organisation-wide event stream.
 *
 * Round-trip `nextCursor` VERBATIM; do not build one. The grammar above is what the
 * server accepts, and a hand-built token asserts both an ordering and a filter set
 * it does not own.
 */
export const TrackEventCursorSchema = z.string().trim().min(1);

/**
 * The organisation's whole event stream, newest first, one keyset page at a time.
 *
 * 🔴 A CURSOR AND NOT AN OFFSET, AND THAT IS FORCED BY THE STREAM RATHER THAN
 * CHOSEN. `TrackEvent` is append-only and read newest-first, so rows land at the
 * head of the very page a reader is walking: `OFFSET n` counts from the head, so
 * every event appended between two calls shifts the window and the reader is served
 * rows it has already seen while never seeing the ones that slid past the boundary.
 * Both faults are silent — each response is a well-formed page of real events.
 *
 * ⚠️ THERE IS DELIBERATELY NO `total`. It would be a `COUNT(*)` over an append-only
 * high-volume table — a second full scan, to answer a number that is stale before
 * the response is written. `nextCursor` is the only completion signal a feed can
 * honestly give.
 *
 * `limit` carries a `.default()` for the reason {@link ListTrackDiaryEntriesQuerySchema}
 * gives: `ListTrackEventsInput.limit` is a required `number`, so the contract is the
 * only place a default can come from.
 */
export const ListOrganizationTrackEventsQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  /**
   * Omitted reads from the newest event. Otherwise: strictly older than this
   * position, under the SAME `since`/`type` it was issued with.
   *
   * 🔴 REPLAYING ONE UNDER DIFFERENT FILTERS IS A 400, NOT A BEST EFFORT. A cursor
   * is a position inside a filtered set: honouring it across a filter change
   * returns a correctly-scoped, correctly-ordered page that STARTS IN THE MIDDLE,
   * and the caller cannot tell that from the top of the feed. Drop the cursor and
   * read the first page of the new filters.
   */
  cursor: TrackEventCursorSchema.optional(),
  /**
   * Inclusive lower bound on `createdAt`, as a full ISO-8601 timestamp.
   *
   * NOT `z.coerce.date()`, and that is the difference between a bound and a guess:
   * coercion is `new Date(value)`, which accepts `"2"` as the first of February
   * 2001. A caller mistyping a date would silently read a completely different
   * window with no error anywhere. This form refuses everything that is not an
   * unambiguous instant.
   */
  since: z.string().datetime({ offset: true }).optional(),
  /** Exact `TrackEvent.type`. Omitted returns every type. */
  type: z.string().trim().min(1).max(128).optional()
});
export type ListOrganizationTrackEventsQuery = z.infer<
  typeof ListOrganizationTrackEventsQuerySchema
>;

export const ListOrganizationTrackEventsResponseSchema = z.object({
  events: z.array(TrackEventSchema),
  /**
   * Feed this back as `cursor` for the next page. `null` means this page reached
   * the end of the stream.
   *
   * ⚠️ A FULL PAGE ALWAYS CARRIES ONE, EVEN WHEN IT WAS THE LAST. "the page is
   * full" and "the page is full and there is nothing after it" are
   * indistinguishable without a second query, so the caller makes one more call and
   * gets an empty page. That is the honest ending; inventing a cheaper one means
   * counting.
   */
  nextCursor: z.string().nullable()
});
export type ListOrganizationTrackEventsResponse = z.infer<
  typeof ListOrganizationTrackEventsResponseSchema
>;

/**
 * 🔴 EVERY EVENT NAMES AN ACTOR, AND ON THIS SURFACE THE AGENT IS REQUIRED.
 *
 * `Track_event_has_an_actor` is `"actorUserId" IS NOT NULL OR "actorAgentId" IS
 * NOT NULL`. A session caller has a user to attribute the event to; an API key
 * may not — a key minted for a machine resolves an owning user on some paths and
 * none on others — so this body demands the agent rather than leaving the actor
 * to a value the surface cannot guarantee. Requiring it here makes the actorless
 * event unrepresentable at the door instead of a 500 from a CHECK.
 *
 * The owning user of the key is attached ALONGSIDE it when there is one: a user
 * acting through an agent is one event with two actors, not two events.
 */
export const AppendTrackEventBodySchema = z
  .object({
    type: z.string().trim().min(1).max(128),
    /** An open object, for the reason {@link OpenTrackAgentBodySchema}'s `inputs` gives. */
    payload: z.record(z.string(), z.unknown()).nullish(),
    actorAgentId: z.string().uuid()
  })
  .strict();
export type AppendTrackEventBody = z.infer<typeof AppendTrackEventBodySchema>;
